// src/components/albums/AlbumSettings.jsx
import React, { useState, useEffect } from 'react';
import { 
  X, 
  Save, 
  Upload, 
  Layout,
  Image as ImageIcon, 
  Eye, 
  Mic, 
  Zap, 
  Flag,
  Volume2, 
  MessageSquare, 
  Shield, 
  BookOpen,
  Heart, 
  Compass,
  MessageCircle,
  Share2,
  LayoutGrid,
  PanelLeft, 
  PanelRight, 
  PanelRightClose,
  Grid,
  RotateCcw,
  Sparkles,
  Briefcase,
  Shirt,
  List,
  Palette,
  Globe,
  Lock,
  Users,
  Type,
  Settings,
  Trash2,
  Loader2,
  AlertCircle,
  Check,
  Play,
  Link,
  Mail,
  Info,
  ShoppingCart,
  BarChart3,
  ExternalLink,
  Monitor,
  Smartphone,
  Tablet,
  Building,
  Camera,
  DollarSign,
  Home,
  Star,
  Tag,
  Calendar,
  Package,
  Building2,  
  Store,
  Target, 
  BarChart,
  GraduationCap,
  TrendingUp,
  HelpCircle,
  CheckCircle,
  Award,
  Lightbulb,
  Trophy,
  FolderKanban, 
  Layers,
  FileText,
  Hash,
  Clock,
  Activity, 

} from 'lucide-react';
import { albumService } from '../../services/albumService';
import storyService from '../../services/storyService';
import { BasicInfoTab } from '../settings/shared/BasicInfoTab';
import { HeaderSettings } from '../settings/shared/HeaderSettings';
import { DesignBrandingSection } from '../settings/shared/DesignSection';
import { ViewSettings } from '../settings/shared/ViewSettings';
import { PrivacySection } from '../settings/shared/PrivacySection';
import { TagInput } from '../common/TagInput';
import { TeamSettingsTab } from '../settings/shared/TeamSettings';

const ALBUM_CATEGORIES = {
  'photo-album': {
    label: 'Photo Album',
    icon: Camera,
    description: 'Traditional photo gallery for memories and events',
    defaultFields: {
      title: true,
      description: true,
      tags: true,
      uploadDate: true,
      location: false,
      photographer: false
    },
    color: '#10b981'
  },
  'menu': {
    label: 'Menu',
    icon: Building,
    description: 'Restaurant menu with dishes, prices, and descriptions',
    defaultFields: {
      title: true,
      description: true,
      mobile: true,
      email: true,
      price: true,
      category: true,
      allergens: true,
      spiceLevel: false,
      availability: true,
      calories: false
    },
    color: '#f59e0b'
  },
  'listing': {
    label: 'Listing',
    icon: Home,
    description: 'Property or service listings with details and pricing',
    defaultFields: {
      title: true,
      description: true,
      price: true,
      mobile: true,
      email: true,
      location: true,
      features: true,
      contact: true,
      availability: true,
      size: false,
      link: true
    },
    color: '#3b82f6'
  },
  'store': {
    label: 'Online Store',
    icon: Store,
    description: 'Product catalog with shopping features',
    defaultFields: {
      title: true,
      description: true,
      price: true,
      mobile: true,
      email: true,
      brand: true,
      quantity: true,
      sku: false,
      category: true,
      rating: true,
      link: true
    },
    color: '#8b5cf6'
  },

  'slides': {
    label: 'Presentation Slides',
    icon: Monitor,
    description: 'Interactive slides for courses, training, and presentations with engagement tracking',
    subcategories: {
      course: {
        label: 'Course Slides',
        icon: BookOpen,
        fields: ['title', 'slideNumber', 'topic', 'learningObjective', 'notes', 'duration', 'resources'],
        interactions: ['understandingLevel', 'questions', 'insights', 'bookmarks'],
        discoveryEnabled: false,
        color: '#3b82f6'
      },
      training: {
        label: 'Training Materials',
        icon: Target,
        fields: ['title', 'slideNumber', 'topic', 'competency', 'practiceExercise', 'duration'],
        interactions: ['understandingLevel', 'questions', 'completionStatus'],
        discoveryEnabled: false,
        color: '#8b5cf6'
      },
      presentation: {
        label: 'Business Presentation',
        icon: BarChart,
        fields: ['title', 'slideNumber', 'section', 'keyPoints', 'notes', 'duration'],
        interactions: ['questions', 'comments', 'polling'],
        discoveryEnabled: false,
        color: '#0ea5e9'
      },
      lecture: {
        label: 'Academic Lecture',
        icon: GraduationCap,
        fields: ['title', 'slideNumber', 'topic', 'chapter', 'learningObjective', 'references', 'duration'],
        interactions: ['understandingLevel', 'questions', 'noteSharing', 'quizzes'],
        discoveryEnabled: false,
        color: '#059669'
      }
    },
    defaultFields: {
      title: true,
      slideNumber: true,
      topic: true,
      description: true,
      learningObjective: false,
      duration: true,
      notes: false,
      resources: false,
      tags: true
    },
    // Slide-specific interaction system
    interactions: {
      understandingLevel: {
        type: 'rating',
        icon: TrendingUp,
        label: 'Understanding Level',
        options: [
          { value: 1, label: 'Need Review', icon: AlertCircle, color: '#ef4444' },
          { value: 2, label: 'Getting There', icon: HelpCircle, color: '#f59e0b' },
          { value: 3, label: 'Understood', icon: CheckCircle, color: '#10b981' },
          { value: 4, label: 'Can Teach', icon: Award, color: '#3b82f6' }
        ],
        trackable: true,
        analytics: ['averageRating', 'distribution', 'trendOverTime']
      },
      questions: {
        type: 'discussion',
        icon: HelpCircle,
        label: 'Ask Questions',
        features: ['threading', 'moderation', 'upvoting', 'expertAnswer'],
        notifications: true,
        trackable: true
      },
      insights: {
        type: 'contribution',
        icon: Lightbulb,
        label: 'Share Insights',
        features: ['richText', 'upvoting', 'highlighting', 'pinning'],
        trackable: true,
        analytics: ['topInsights', 'engagementRate']
      },
      polling: {
        type: 'poll',
        icon: BarChart,
        label: 'Live Polls',
        features: ['multipleChoice', 'realTimeResults', 'anonymous'],
        trackable: true
      },
      bookmarks: {
        type: 'bookmark',
        icon: BookOpen,
        label: 'Bookmarks',
        personal: true,
        trackable: true
      }
    },
    // Slide streaming and presentation mode
    streaming: {
      enabled: true,
      features: ['livePresentation', 'syncedViewing', 'laserPointer', 'drawings', 'annotations'],
      controls: ['play', 'pause', 'next', 'previous', 'jump', 'loop'],
      settings: ['autoAdvance', 'transitionEffect', 'timerDisplay']
    },
    color: '#3b82f6'
  },

  'teams': {
    label: 'Team Management',
    icon: Users,
    description: 'Visual team directory with updates, performance tracking, and wellbeing monitoring',
    
    subcategories: {
      corporate: {
        label: 'Corporate Team',
        icon: Briefcase,
        fields: ['name', 'position', 'department', 'photo', 'status', 'wellbeing', 'performance', 'attendance', 'projects', 'availability'],
        interactions: ['quickUpdates', 'statusFlags', 'comments', 'recognition', 'goals'],
        discoveryEnabled: false,
        color: '#0ea5e9'
      },
      sports: {
        label: 'Sports Team',
        icon: Trophy,
        fields: ['name', 'position', 'number', 'photo', 'fitness', 'performance', 'injuries', 'attendance', 'achievements'],
        interactions: ['quickUpdates', 'statusFlags', 'comments', 'voting'],
        discoveryEnabled: false,
        color: '#f59e0b'
      },
      project: {
        label: 'Project Team',
        icon: FolderKanban,
        fields: ['name', 'role', 'photo', 'taskProgress', 'availability', 'workload', 'skills', 'contributions'],
        interactions: ['quickUpdates', 'statusFlags', 'comments', 'taskAssignment'],
        discoveryEnabled: false,
        color: '#8b5cf6'
      },
      volunteer: {
        label: 'Volunteer Group',
        icon: Heart,
        fields: ['name', 'role', 'photo', 'hours', 'contributions', 'availability', 'wellbeing'],
        interactions: ['quickUpdates', 'statusFlags', 'comments', 'appreciation'],
        discoveryEnabled: false,
        color: '#ec4899'
      }
    },
    
    defaultFields: {
      name: true,
      position: true,
      department: false,
      photo: true,
      bio: true,
      status: true,
      wellbeing: false,        // NEW
      attendance: false,       // NEW
      performance: false,
      lastUpdate: true,
      contact: false,
      achievements: false,
      tags: true
    },
    
    // ========== NEW: QUICK STATUS BUTTONS ==========
    quickStatusOptions: {
      // Managers can toggle which status buttons appear
      wellbeing: {
        label: 'Wellbeing Indicators',
        icon: Heart,
        enabled: false,  // Manager toggles on/off
        options: [
          { 
            id: 'engaged', 
            label: 'Engaged', 
            icon: '🔥', 
            color: '#10b981', 
            description: 'Actively engaged and motivated'
          },
          { 
            id: 'stressed', 
            label: 'Stressed', 
            icon: '😰', 
            color: '#ef4444', 
            description: 'Showing signs of stress'
          },
          { 
            id: 'focused', 
            label: 'Focused', 
            icon: '🎯', 
            color: '#3b82f6', 
            description: 'Highly focused on tasks'
          },
          { 
            id: 'needsSupport', 
            label: 'Needs Support', 
            icon: '🤝', 
            color: '#f59e0b', 
            description: 'Requires additional support'
          },
          { 
            id: 'thriving', 
            label: 'Thriving', 
            icon: '⭐', 
            color: '#8b5cf6', 
            description: 'Performing excellently'
          },
          { 
            id: 'overwhelmed', 
            label: 'Overwhelmed', 
            icon: '😓', 
            color: '#dc2626', 
            description: 'Too much on their plate'
          },
          { 
            id: 'satisfied', 
            label: 'Satisfied', 
            icon: '😊', 
            color: '#059669', 
            description: 'Content and satisfied'
          },
          { 
            id: 'neutral', 
            label: 'Neutral', 
            icon: '😐', 
            color: '#6b7280', 
            description: 'No significant concerns'
          }
        ],
        trackHistory: true,
        showOnCard: true,
        requireNotes: false  // Optional notes when setting status
      },
      
      attendance: {
        label: 'Attendance Tracking',
        icon: Calendar,
        enabled: false,
        options: [
          { 
            id: 'present', 
            label: 'Present', 
            icon: '✓', 
            color: '#10b981', 
            description: 'In attendance today'
          },
          { 
            id: 'absent', 
            label: 'Absent', 
            icon: '✗', 
            color: '#ef4444', 
            description: 'Not present today'
          },
          { 
            id: 'leave', 
            label: 'On Leave', 
            icon: '🏖️', 
            color: '#3b82f6', 
            description: 'Approved leave'
          },
          { 
            id: 'sick', 
            label: 'Sick Leave', 
            icon: '🤒', 
            color: '#f59e0b', 
            description: 'Out sick'
          },
          { 
            id: 'remote', 
            label: 'Remote', 
            icon: '🏠', 
            color: '#8b5cf6', 
            description: 'Working remotely'
          },
          { 
            id: 'late', 
            label: 'Late', 
            icon: '⏰', 
            color: '#f97316', 
            description: 'Arrived late'
          }
        ],
        trackHistory: true,
        showOnCard: true,
        autoResetDaily: true,  // Reset to null at midnight
        requireNotes: false
      },
      
      performance: {
        label: 'Performance Quick Rating',
        icon: TrendingUp,
        enabled: false,
        type: 'rating',  // Different from status buttons
        options: [
          { value: 1, label: 'Needs Improvement', icon: '📉', color: '#ef4444' },
          { value: 2, label: 'Below Expectations', icon: '👎', color: '#f59e0b' },
          { value: 3, label: 'Meets Expectations', icon: '👍', color: '#10b981' },
          { value: 4, label: 'Exceeds Expectations', icon: '🌟', color: '#3b82f6' },
          { value: 5, label: 'Outstanding', icon: '🏆', color: '#8b5cf6' }
        ],
        trackHistory: true,
        showOnCard: true,
        requireNotes: true  // Must add notes when rating
      },
      
      mood: {
        label: 'Team Mood',
        icon: Activity,
        enabled: false,
        options: [
          { id: 'energetic', label: 'Energetic', icon: '⚡', color: '#f59e0b' },
          { id: 'calm', label: 'Calm', icon: '🧘', color: '#10b981' },
          { id: 'tired', label: 'Tired', icon: '😴', color: '#6b7280' },
          { id: 'motivated', label: 'Motivated', icon: '💪', color: '#3b82f6' },
          { id: 'frustrated', label: 'Frustrated', icon: '😤', color: '#ef4444' }
        ],
        trackHistory: true,
        showOnCard: false,  // Don't show on public card
        requireNotes: false
      },
      
      availability: {
        label: 'Availability Status',
        icon: Clock,
        enabled: false,
        options: [
          { id: 'available', label: 'Available', icon: '🟢', color: '#10b981' },
          { id: 'busy', label: 'Busy', icon: '🔴', color: '#ef4444' },
          { id: 'meeting', label: 'In Meeting', icon: '📅', color: '#f59e0b' },
          { id: 'away', label: 'Away', icon: '⏸️', color: '#6b7280' },
          { id: 'dnd', label: 'Do Not Disturb', icon: '🚫', color: '#dc2626' }
        ],
        trackHistory: false,  // Current state only
        showOnCard: true,
        requireNotes: false
      }
    },
    
    // ========== TEAM-SPECIFIC INTERACTIONS (Updated) ==========
    interactions: {
      quickUpdates: {
        type: 'quick-action',
        icon: Zap,
        label: 'Quick Status Update',
        features: ['oneClickStatus', 'bulkUpdate', 'scheduledReset', 'notifications'],
        description: 'Instant status updates with 1-click buttons',
        permissions: 'manager',
        visibility: 'team',
        trackable: true,
        analytics: ['statusDistribution', 'trends', 'alerts']
      },
      
      statusFlags: {
        type: 'indicator',
        icon: Flag,
        label: 'Status Flags',
        features: ['visual indicators', 'color coding', 'badges', 'filters'],
        description: 'Visual badges showing current member status',
        visibility: 'team',
        trackable: true
      },
      
      updates: {
        type: 'timeline',
        icon: Calendar,
        label: 'Member Updates',
        features: ['richText', 'media', 'statusFlags', 'milestones', 'history'],
        permissions: 'manager',
        visibility: 'team',
        notifications: true,
        trackable: true
      },
      
      comments: {
        type: 'moderated-discussion',
        icon: MessageCircle,
        label: 'Team Comments',
        features: ['threading', 'managerModeration', 'mentions', 'reactions'],
        moderation: {
          mode: 'approval',
          approvers: ['manager', 'admin'],
          autoApprove: ['managers'],
          filterRules: ['profanity', 'personalInfo']
        },
        notifications: true,
        trackable: true
      },
      
      recognition: {
        type: 'award',
        icon: Award,
        label: 'Recognition',
        features: ['badges', 'certificates', 'shoutouts', 'publicDisplay'],
        permissions: ['manager', 'peers'],
        trackable: true,
        analytics: ['topPerformers', 'recognitionTrends']
      },
      
      performance: {
        type: 'metrics',
        icon: TrendingUp,
        label: 'Performance Tracking',
        features: ['kpiTracking', 'goals', 'reviews', 'charts', 'quickRatings'],
        permissions: 'manager',
        visibility: 'private',
        trackable: true,
        analytics: ['teamPerformance', 'individualMetrics', 'trends']
      },
      
      goals: {
        type: 'objective',
        icon: Target,
        label: 'Goals & Objectives',
        features: ['goalSetting', 'progress', 'milestones', 'completion'],
        permissions: 'manager',
        trackable: true
      },
      
      wellbeingAlerts: {  // NEW
        type: 'notification',
        icon: AlertCircle,
        label: 'Wellbeing Alerts',
        features: ['autoAlerts', 'thresholds', 'escalation', 'privateNotifications'],
        description: 'Automated alerts for wellbeing concerns',
        permissions: 'manager',
        triggers: {
          stressed: { threshold: 3, period: 'week' },  // 3 times in a week
          overwhelmed: { threshold: 2, period: 'week' },
          needsSupport: { threshold: 1, period: 'immediate' }
        },
        trackable: true
      }
    },
    
    // ========== ANALYTICS & REPORTING ==========
    analytics: {
      enabled: true,
      dashboards: {
        wellbeing: {
          label: 'Team Wellbeing Dashboard',
          metrics: ['overallMood', 'stressLevels', 'supportNeeded', 'trends'],
          charts: ['timeline', 'heatmap', 'distribution'],
          alerts: true
        },
        attendance: {
          label: 'Attendance Overview',
          metrics: ['presentRate', 'absenceRate', 'leaveUsage', 'patterns'],
          charts: ['calendar', 'trends', 'comparison'],
          export: true
        },
        performance: {
          label: 'Performance Metrics',
          metrics: ['averageRating', 'topPerformers', 'improvementAreas', 'goals'],
          charts: ['bar', 'line', 'radar'],
          private: true
        },
        engagement: {
          label: 'Engagement Tracking',
          metrics: ['updateFrequency', 'commentActivity', 'recognitionGiven', 'responseTime'],
          charts: ['activity', 'trends', 'comparison']
        }
      }
    },
    
    // Management features (existing + enhanced)
    management: {
      roles: ['admin', 'manager', 'teamLead', 'member', 'viewer'],
      permissions: {
        admin: ['all'],
        manager: ['editMembers', 'postUpdates', 'setQuickStatus', 'moderateComments', 'viewAnalytics', 'manageRoles'],
        teamLead: ['postUpdates', 'viewTeam', 'addComments', 'setQuickStatus'],
        member: ['viewTeam', 'addModeratedComments'],
        viewer: ['viewTeam']
      },
      features: ['hierarchy', 'departments', 'reporting', 'orgChart', 'bulkActions']
    },
    
    color: '#0ea5e9'
  },

  'students': {
    label: 'Student Portfolio',
    icon: GraduationCap,
    description: 'Visual student directory with academic progress, achievements, and educational tracking',
    subcategories: {
      classroom: {
        label: 'Classroom',
        icon: Users,
        fields: ['name', 'photo', 'studentId', 'grade', 'attendance', 'behavior', 'assignments', 'progress'],
        interactions: ['progressUpdates', 'parentComments', 'achievements', 'concerns'],
        discoveryEnabled: false,
        color: '#3b82f6'
      },
      course: {
        label: 'Course Students',
        icon: BookOpen,
        fields: ['name', 'photo', 'studentId', 'courseProgress', 'assignments', 'quizScores', 'participation'],
        interactions: ['progressUpdates', 'feedback', 'discussions'],
        discoveryEnabled: false,
        color: '#8b5cf6'
      },
      grade: {
        label: 'Grade Level',
        icon: Layers,
        fields: ['name', 'photo', 'studentId', 'subjects', 'overallGrade', 'attendance', 'achievements'],
        interactions: ['progressUpdates', 'parentComments', 'achievements'],
        discoveryEnabled: false,
        color: '#059669'
      },
      school: {
        label: 'School Wide',
        icon: Building,
        fields: ['name', 'photo', 'studentId', 'grade', 'class', 'achievements', 'activities'],
        interactions: ['achievements', 'activities', 'events'],
        discoveryEnabled: false,
        color: '#f59e0b'
      }
    },
    defaultFields: {
      name: true,
      photo: true,
      studentId: false,
      grade: true,
      class: true,
      attendance: false,
      progress: true,
      achievements: true,
      lastUpdate: true,
      notes: false,
      tags: true
    },
    // Student-specific interaction system
    interactions: {
      progressUpdates: {
        type: 'academic-update',
        icon: TrendingUp,
        label: 'Progress Updates',
        features: ['gradeTracking', 'milestones', 'improvements', 'concerns', 'richText', 'media'],
        permissions: ['teacher', 'admin'],
        visibility: ['teacher', 'parent', 'student'],
        notifications: true,
        trackable: true,
        analytics: ['progressTrends', 'subjectPerformance']
      },
      parentComments: {
        type: 'family-communication',
        icon: MessageCircle,
        label: 'Parent/Guardian Messages',
        features: ['threading', 'privateMessages', 'teacherModeration', 'scheduling'],
        permissions: ['parent', 'guardian', 'teacher'],
        moderation: {
          mode: 'monitored',
          visibility: ['teacher', 'admin'],
          notifications: true
        },
        trackable: true
      },
      achievements: {
        type: 'milestone',
        icon: Award,
        label: 'Achievements & Awards',
        features: ['badges', 'certificates', 'publicDisplay', 'categories', 'media'],
        permissions: ['teacher', 'admin'],
        visibility: 'public',
        categories: ['academic', 'behavioral', 'athletic', 'artistic', 'leadership', 'community'],
        trackable: true
      },
      behavior: {
        type: 'behavioral-tracking',
        icon: CheckCircle,
        label: 'Behavior Notes',
        features: ['positiveReinforcement', 'concernFlags', 'patterns', 'interventions'],
        permissions: ['teacher', 'counselor', 'admin'],
        visibility: ['teacher', 'parent', 'admin'],
        trackable: true,
        analytics: ['behaviorTrends', 'interventionEffectiveness']
      },
      assignments: {
        type: 'work-tracking',
        icon: FileText,
        label: 'Assignments & Submissions',
        features: ['submission', 'grading', 'feedback', 'rubrics', 'lateTracking'],
        permissions: ['teacher', 'student'],
        trackable: true,
        analytics: ['completionRate', 'averageGrade', 'submissionPatterns']
      },
      attendance: {
        type: 'tracking',
        icon: Calendar,
        label: 'Attendance',
        features: ['checkIn', 'absences', 'tardiness', 'excusedRecords'],
        permissions: ['teacher', 'admin'],
        trackable: true,
        analytics: ['attendanceRate', 'patterns', 'concerns']
      }
    },
    // Educational organization features
    organization: {
      structure: {
        levels: ['school', 'grade', 'class', 'subject', 'group'],
        hierarchical: true,
        multipleGalleries: true
      },
      galleries: {
        byClass: true,
        byGrade: true,
        bySubject: true,
        byCourse: true,
        byGroup: true,
        customCategories: true
      },
      features: ['gradebook', 'reportCards', 'parentPortal', 'studentPortal', 'exportData']
    },
    // Privacy and compliance
    privacy: {
      ferpaCompliant: true,
      gdprCompliant: true,
      features: ['parentalConsent', 'photoPermissions', 'dataRetention', 'accessLogs'],
      controls: ['whoCanView', 'whoCanComment', 'dataSharing', 'exportOptions']
    },
    color: '#059669'
  },

  'showcase': {
    label: 'Showcase',
    icon: Star,
    description: 'Professional portfolio and discovery showcase',
    subcategories: {
      fashion: {
        label: 'Fashion',
        icon: Shirt,
        fields: ['title', 'description', 'brand', 'designer', 'collection', 'season', 'category', 'tags', 'link'],
        discoveryEnabled: true,
        color: '#ec4899'
      },
      beauty: {
        label: 'Beauty',
        icon: Sparkles,
        fields: ['title', 'description', 'brand', 'productType', 'tags', 'price', 'link'],
        discoveryEnabled: true,
        color: '#f97316'
      },
      products: {
        label: 'Products',
        icon: Package,
        fields: ['title', 'description', 'brand', 'price', 'category', 'features', 'tags', 'link'],
        discoveryEnabled: true,
        color: '#8b5cf6'
      },
      projects: {
        label: 'Projects',
        icon: Briefcase,
        fields: ['title', 'description', 'client', 'projectDate', 'category', 'skills', 'link'],
        discoveryEnabled: true,
        color: '#0ea5e9'
      },
      photography: {
        label: 'Photography',
        icon: Camera,
        fields: ['title', 'description', 'location', 'projectDate', 'client', 'category', 'tags'],
        discoveryEnabled: true,
        color: '#14b8a6'
      },
      art: {
        label: 'Art & Design',
        icon: Palette,
        fields: ['title', 'description', 'medium', 'dimensions', 'projectDate', 'category', 'tags', 'link'],
        discoveryEnabled: true,
        color: '#a855f7'
      }
    },
    defaultFields: {
      title: true,
      description: true,
      category: true,
      tags: true,
      link: true
    },
    color: '#ef4444'
  }
};

// All possible media card fields
const MEDIA_CARD_FIELDS = {
  // Basic Info
  title: { label: 'Title', type: 'text', icon: Type },
  description: { label: 'Description', type: 'textarea', icon: MessageCircle },
  tags: { label: 'Tags', type: 'tags', icon: Tag },
  mobile: { label: 'Mobile Number', type: 'phone', icon: Smartphone },
  email: { label: 'Email Address', type: 'email', icon: Mail },
  
  // Commercial Fields
  price: { label: 'Price', type: 'currency', icon: DollarSign },
  brand: { label: 'Brand Name', type: 'text', icon: Building2 },
  brandLogo: { label: 'Brand Logo', type: 'image', icon: ImageIcon },
  quantity: { label: 'Quantity/Stock', type: 'number', icon: Package },
  sku: { label: 'SKU/Code', type: 'text', icon: Tag },
  rating: { label: 'Rating', type: 'rating', icon: Star },
  link: { label: 'External Link', type: 'url', icon: Link },
  
  // Location & Contact
  location: { label: 'Location', type: 'text', icon: Globe },
  contact: { label: 'Contact Info', type: 'contact', icon: Users },
  
  // Food/Menu Specific
  category: { label: 'Category', type: 'select', icon: Grid },
  allergens: { label: 'Allergens', type: 'tags', icon: AlertCircle },
  spiceLevel: { label: 'Spice Level', type: 'rating', icon: Star },
  calories: { label: 'Calories', type: 'number', icon: Heart },
  
  // Service/Listing Specific
  features: { label: 'Features', type: 'tags', icon: Check },
  availability: { label: 'Availability', type: 'status', icon: Eye },
  size: { label: 'Size/Dimensions', type: 'text', icon: Building },
  
  // Portfolio/Showcase Specific
  client: { label: 'Client', type: 'text', icon: Users },
  projectDate: { label: 'Project Date', type: 'date', icon: Calendar },
  skills: { label: 'Skills/Tools', type: 'tags', icon: Settings },
  awards: { label: 'Awards', type: 'tags', icon: Star },

  slideNumber: {
    label: 'Slide Number',
    type: 'number',
    icon: Hash,
    category: 'slide',
    sortable: true,
    searchable: true
  },
  topic: {
    label: 'Topic',
    type: 'text',
    icon: BookOpen,
    category: 'slide',
    searchable: true
  },
  learningObjective: {
    label: 'Learning Objective',
    type: 'textarea',
    icon: Target,
    category: 'slide',
    searchable: true
  },
  duration: {
    label: 'Duration (minutes)',
    type: 'number',
    icon: Clock,
    category: 'slide',
    sortable: true
  },
  resources: {
    label: 'Resources',
    type: 'list',
    icon: Link,
    category: 'slide',
    multiple: true
  },
  competency: {
    label: 'Competency',
    type: 'text',
    icon: Award,
    category: 'slide'
  },
  chapter: {
    label: 'Chapter',
    type: 'text',
    icon: BookOpen,
    category: 'slide',
    searchable: true
  },
  
  // Team-specific fields
  position: {
    label: 'Position/Role',
    type: 'text',
    icon: Briefcase,
    category: 'team',
    searchable: true
  },
  department: {
    label: 'Department',
    type: 'text',
    icon: Building,
    category: 'team',
    filterable: true,
    searchable: true
  },
  status: {
    label: 'Status',
    type: 'select',
    icon: Activity,
    category: 'team',
    options: ['active', 'away', 'busy', 'offline'],
    filterable: true
  },
  performance: {
    label: 'Performance Rating',
    type: 'rating',
    icon: TrendingUp,
    category: 'team',
    private: true,
    sortable: true
  },
  projects: {
    label: 'Current Projects',
    type: 'list',
    icon: FolderKanban,
    category: 'team',
    multiple: true
  },
  availability: {
    label: 'Availability',
    type: 'text',
    icon: Calendar,
    category: 'team'
  },
  lastUpdate: {
    label: 'Last Update',
    type: 'datetime',
    icon: Clock,
    category: 'team',
    automatic: true,
    sortable: true
  },
  
  // Student-specific fields
  studentId: {
    label: 'Student ID',
    type: 'text',
    icon: Hash,
    category: 'student',
    private: true,
    searchable: true
  },
  grade: {
    label: 'Grade Level',
    type: 'text',
    icon: Layers,
    category: 'student',
    filterable: true,
    searchable: true
  },
  class: {
    label: 'Class/Homeroom',
    type: 'text',
    icon: Users,
    category: 'student',
    filterable: true,
    searchable: true
  },
  attendance: {
    label: 'Attendance Rate',
    type: 'percentage',
    icon: Calendar,
    category: 'student',
    sortable: true,
    private: true
  },
  progress: {
    label: 'Overall Progress',
    type: 'rating',
    icon: TrendingUp,
    category: 'student',
    sortable: true
  },
  achievements: {
    label: 'Achievements',
    type: 'list',
    icon: Award,
    category: 'student',
    multiple: true
  },
  subjects: {
    label: 'Subjects',
    type: 'list',
    icon: BookOpen,
    category: 'student',
    multiple: true,
    filterable: true
  },
  behavior: {
    label: 'Behavior Rating',
    type: 'rating',
    icon: CheckCircle,
    category: 'student',
    private: true
  },
  
  // Metadata
  uploadDate: { label: 'Upload Date', type: 'date', icon: Calendar },
  photographer: { label: 'Photographer', type: 'text', icon: Camera }
};

export const INTERACTION_ANALYTICS = {
  slides: {
    metrics: [
      { key: 'avgUnderstandingLevel', label: 'Average Understanding', type: 'rating' },
      { key: 'questionCount', label: 'Questions Asked', type: 'number' },
      { key: 'insightCount', label: 'Insights Shared', type: 'number' },
      { key: 'engagementRate', label: 'Engagement Rate', type: 'percentage' },
      { key: 'completionRate', label: 'Completion Rate', type: 'percentage' },
      { key: 'difficultSlides', label: 'Difficult Slides', type: 'list' },
      { key: 'popularSlides', label: 'Popular Slides', type: 'list' }
    ],
    visualizations: ['lineChart', 'barChart', 'heatmap', 'distribution']
  },
  teams: {
    metrics: [
      { key: 'teamEngagement', label: 'Team Engagement', type: 'percentage' },
      { key: 'updateFrequency', label: 'Update Frequency', type: 'number' },
      { key: 'commentActivity', label: 'Comment Activity', type: 'number' },
      { key: 'recognitionCount', label: 'Recognitions Given', type: 'number' },
      { key: 'performanceTrend', label: 'Performance Trend', type: 'trend' },
      { key: 'goalCompletion', label: 'Goal Completion', type: 'percentage' },
      { key: 'topPerformers', label: 'Top Performers', type: 'list' }
    ],
    visualizations: ['lineChart', 'barChart', 'radarChart', 'orgChart']
  },
  students: {
    metrics: [
      { key: 'classAverage', label: 'Class Average', type: 'grade' },
      { key: 'attendanceRate', label: 'Attendance Rate', type: 'percentage' },
      { key: 'assignmentCompletion', label: 'Assignment Completion', type: 'percentage' },
      { key: 'achievementCount', label: 'Total Achievements', type: 'number' },
      { key: 'parentEngagement', label: 'Parent Engagement', type: 'percentage' },
      { key: 'improvementTrend', label: 'Improvement Trend', type: 'trend' },
      { key: 'atRiskStudents', label: 'Students Needing Support', type: 'list' }
    ],
    visualizations: ['lineChart', 'barChart', 'gradeDistribution', 'progressTimeline']
  }
};

export default {
  ALBUM_CATEGORIES,
  MEDIA_CARD_FIELDS,
  INTERACTION_ANALYTICS
};

export const AlbumSettings = ({ book, bookId, isOpen, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({
    // Album Category
    albumCategory: 'photo-album',
    
    // Media Card Fields Configuration
    enabledFields: {},
    fieldSettings: {},
    
    // Basic Info
    title: '',
    description: '',
    coverImageUrl: '',
    logoUrl: '',
    headerBackgroundUrl: '',
    
    // Custom Styling 
    primaryColor: '#0795ff',
    backgroundColor: '#ffffff',
    headerColor: '#ffffff',
    headerTextColor: '#333333',
    headerOpacity: '100',
    accentColor: '#f59e0b',
    textColor: '#1f2937',
    fontFamily: 'Inter, sans-serif',
    headerRadius: '0px',
    cardRadius: '12px',
    shadowLevel: 'medium',
    logoInvert: 0,
    gallerySidebarBg: '#f9fafb',
    gallerySidebarShadow:'#000000',
    
    // Display Settings
    defaultView: 'masonry',
    columnsBreakpoints: {
      mobile: 1,
      tablet: 2,
      desktop: 3,
      wide: 4
    },
    cardStyle: 'modern',
    showMediaTitles: true,
    showMediaDescriptions: true,
    showTags: true,
    
    // Interaction Settings
    allowLikes: true,
    allowComments: true,
    allowSharing: true,
    allowDownloads: true,
    allowShopping: false, 
    allowBooking: false,
    showViewCounts: true,
    showInteractionStats: true,

    transactions: {
      booking: {
        enabled: false,
        title: 'Book Now',
        description: 'Schedule an appointment',
        url: '',
        color: '#3b82f6',
        icon: 'calendar'
      },
      shopping: {
        enabled: false,
        title: 'Shop Now',
        description: 'Browse our products',
        url: '',
        color: '#10b981',
        icon: 'shopping-cart'
      }
    },
    // Gallery Settings
    autoCreateFirstGallery: true,
    defaultGalleryName: 'Main Gallery',
    showGalleryTabs: true,
    allowMultipleGalleries: true,
    
    // Media Settings
    maxMediaSize: 10,
    autoGenerateThumbnails: true,
    enableImageOptimization: true,
    allowedFileTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4'],
    
    // ACCESS LEVEL
    accessLevel: 'public', // 'public', 'private', 'password'
    accessPassword: '',
    requireAuth: false,
    
    // AUTHORIZED USERS (for private access)
    authorizedUsers: [],
    
    // MANAGEMENT ACCESS - NEW FIELDS
    managementAccess: {
      allOrgUsers: false,
      specificEmails: []
    },
    
    // VIEW ACCESS
    viewAccess: {
      enablePublicView: true,
      enableManagementView: true,
      allowViewToggle: true,
      defaultView: 'public',
      requireAuthForManagement: true
    },

    // PRIVACY SETTINGS
    allowAnonymousViews: true,
    requireLoginForContact: false,
    moderateSubmissions: false,
    published: true,

    enableFlipCards: false,
    flipDirection: 'horizontal',
    flipDuration: '0.6s',
    autoFlipOnHover: false,
    showFlipIndicator: false,
    
    // SEO & Domain
    customDomain: '',
    customPath: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    favicon: book.favicon || '',
    ogImage: book.ogImage || '',
    
    // Element Visibility
    showLogo: true,
    showTitle: true,
    showDescription: false,
    showSearch: true,
    showShareButton: true,
    showAccessLevel: true,
    showFilterControls: true,
    showGallerySelector: true,
    showViewModeToggle: true,
    showOwnerControls: true,

    // Styling
    headerHeight: 80,
    logoHeight: 40,
    position: 'sticky', // sticky|fixed|static
    layout: 'default', // default|centered|minimal|compact

    // Search Configuration
    searchPlaceholder: 'Search media...',
    searchPosition: 'center', // center|right|left|dropdown
    enableLiveSearch: true,
    showSearchOnMobile: false,

    // User Profile
    userProfileVisibility: 'auto', // auto|always|authenticated|never
    showProfileForPublic: false,
    requireLoginToView: false,

    // Discovery fields
    discoveryEnabled: false,
    showcaseSubcategory: '',
    discoveryTags: [],
    featuredItemsLimit: 6,
    discoveryFilters: {
      category: true,
      tags: true,
      dateRange: false,
      featured: true
    },
    discoveryLayout: 'masonry',
    
    // Advanced Settings
    enableAnalytics: true,
    enableRightClickProtection: false,
    customCSS: ''
  });
  
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [previewMode, setPreviewMode] = useState('desktop');
  
  // File states
  const [logoFile, setLogoFile] = useState(null);
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [headerBackgroundFile, setHeaderBackgroundFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null); // ADD THIS LINE
  const [ogImageFile, setOgImageFile] = useState(null); 
  
  // Preview states
  const [logoPreview, setLogoPreview] = useState('');
  const [coverImagePreview, setCoverImagePreview] = useState('');
  const [headerBackgroundPreview, setHeaderBackgroundPreview] = useState('');
  const [faviconPreview, setFaviconPreview] = useState(''); // ADD THIS LINE
  const [ogImagePreview, setOgImagePreview] = useState('');
  const [albumStories, setAlbumStories] = useState([]);

  // Initialize form data when book changes
  useEffect(() => {
    if (book) {
      const category = book.albumSettings?.albumCategory || 'photo-album';
      const subcategory = book.albumSettings?.albumSubcategory || null;
      const categoryPreset = ALBUM_CATEGORIES[category];
      
      setFormData({
        // Album Category
        albumCategory: category,
        albumSubcategory: subcategory,

        quickStatusOptions: book.albumSettings?.quickStatusOptions || {},
        teamRecordCategories: book.albumSettings?.teamRecordCategories || [],
        wellbeingAlerts: book.albumSettings?.wellbeingAlerts || {
          stressed: { enabled: false, threshold: 3, period: 'week' },
          overwhelmed: { enabled: false, threshold: 2, period: 'week' },
          needsSupport: { enabled: false, threshold: 1, period: 'immediate' }
        },

        // Initialize enabled fields from category preset or existing settings
        enabledFields: book.albumSettings?.enabledFields || categoryPreset?.defaultFields || {},
        fieldSettings: book.albumSettings?.fieldSettings || {},
        
        // Existing fields (keeping your current logic)
        title: book.botName || book.title || '',
        botName: book.botName || '',
        description: book.description || '',
        coverImageUrl: book.coverImageUrl || '',
        logoUrl: book.logoUrl || '',
        headerBackgroundUrl: book.headerBackgroundUrl || '',
        
        // Custom Styling
        primaryColor: book.styling?.primaryColor || '#0795ff',
        backgroundColor: book.styling?.backgroundColor || '#ffffff',
        headerColor: book.styling?.headerColor || '#ffffff',
        headerTextColor: book.styling?.headerTextColor || '#333333',
        headerOpacity: book.styling?.headerOpacity || '100',
        textColor: book?.styling?.textColor || '#1f2937',
        accentColor: book.styling?.accentColor || '#f59e0b',
        fontFamily: book.styling?.fontFamily || 'Inter, sans-serif',
        headerRadius: book.styling?.headerRadius || '0px',
        cardRadius: book.styling?.cardRadius || '12px',
        shadowLevel: book.styling?.shadowLevel || 'medium',
        logoInvert: book.styling?.logoInvert || 0,
        gallerySidebarBg: book.styling?.gallerySidebarBg || '#f9fafb',
        gallerySidebarShadow: book.styling?.gallerySidebarShadow || '#000000',

        headerSettings: {
          // Element Visibility
          showLogo: book.albumSettings?.headerSettings?.showLogo ?? true,
          showTitle: book.albumSettings?.headerSettings?.showTitle ?? true,
          showDescription: book.albumSettings?.headerSettings?.showDescription ?? false,
          showSearch: book.albumSettings?.headerSettings?.showSearch ?? true,
          showShareButton: book.albumSettings?.headerSettings?.showShareButton ?? true,
          showAccessLevel: book.albumSettings?.headerSettings?.showAccessLevel ?? true,
          showFilterControls: book.albumSettings?.headerSettings?.showFilterControls ?? true,
          showGallerySelector: book.albumSettings?.headerSettings?.showGallerySelector ?? true,
          showViewModeToggle: book.albumSettings?.headerSettings?.showViewModeToggle ?? true,
          showOwnerControls: book.albumSettings?.headerSettings?.showOwnerControls ?? true,

          // Styling
          headerHeight: book.albumSettings?.headerSettings?.headerHeight || 80,
          logoHeight: book.albumSettings?.headerSettings?.logoHeight || 40,
          position: book.albumSettings?.headerSettings?.position || 'sticky',
          layout: book.albumSettings?.headerSettings?.layout || 'default',

          // Search Configuration
          searchPlaceholder: book.albumSettings?.headerSettings?.searchPlaceholder || 'Search media...',
          searchPosition: book.albumSettings?.headerSettings?.searchPosition || 'center',
          enableLiveSearch: book.albumSettings?.headerSettings?.enableLiveSearch ?? true,
          showSearchOnMobile: book.albumSettings?.headerSettings?.showSearchOnMobile ?? false,

          // User Profile
          userProfileVisibility: book.albumSettings?.headerSettings?.userProfileVisibility || 'auto',
          showProfileForPublic: book.albumSettings?.headerSettings?.showProfileForPublic ?? false,
          requireLoginToView: book.albumSettings?.headerSettings?.requireLoginToView ?? false,

          ...book.albumSettings?.headerSettings
        },

        sidestrip: {
          enabled: book.albumSettings?.sidestrip?.enabled || false,
          position: book.albumSettings?.sidestrip?.position || 'right',
          showThumbnails: book.albumSettings?.sidestrip?.showThumbnails !== false,
          showLabels: book.albumSettings?.sidestrip?.showLabels !== false,
          showCounts: book.albumSettings?.sidestrip?.showCounts || false,
          collapsible: book.albumSettings?.sidestrip?.collapsible !== false,
          width: book.albumSettings?.sidestrip?.width || 'medium',
          thumbnailSize: book.albumSettings?.sidestrip?.thumbnailSize || 'medium'
        },
        
        // Display Settings
        defaultView: book.albumSettings?.defaultView || 'masonry',
        columnsBreakpoints: book.albumSettings?.columnsBreakpoints || {
          mobile: 1,
          tablet: 2,
          desktop: 3,
          wide: 4
        },
        cardStyle: book.albumSettings?.cardStyle || 'modern',
        showMediaTitles: book.albumSettings?.showMediaTitles !== false,
        showMediaDescriptions: book.albumSettings?.showMediaDescriptions !== false,
        showTags: book.albumSettings?.showTags !== false,
        
        // Interaction Settings
        allowLikes: book.allowLikes !== false,
        allowComments: book.allowComments !== false,
        allowSharing: book.allowSharing !== false,
        allowDownloads: book.allowDownloads !== false,
        allowShopping: book.allowShopping !== false,
        allowBooking: book.allowBooking !== false,
        showViewCounts: book.showViewCounts !== false,
        showInteractionStats: book.albumSettings?.showInteractionStats !== false,

        transactions: book.transactions || {
          booking: {
            enabled: false,
            title: 'Book Now',
            description: 'Schedule an appointment', 
            url: '',
            color: '#3b82f6'
          },
          shopping: {
            enabled: false,
            title: 'Shop Now',
            description: 'Browse our products',
            url: '',
            color: '#10b981'
          }
        },
        // Gallery Settings
        autoCreateFirstGallery: book.albumSettings?.autoCreateFirstGallery !== false,
        defaultGalleryName: book.albumSettings?.defaultGalleryName || 'Main Gallery',
        showGalleryTabs: book.albumSettings?.showGalleryTabs !== false,
        allowMultipleGalleries: book.albumSettings?.allowMultipleGalleries !== false,
        
        // Media Settings
        maxMediaSize: book.albumSettings?.maxMediaSize || 10,
        autoGenerateThumbnails: book.albumSettings?.autoGenerateThumbnails !== false,
        enableImageOptimization: book.albumSettings?.enableImageOptimization !== false,
        allowedFileTypes: book.albumSettings?.allowedFileTypes || ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4'],
        
        // ACCESS & PRIVACY - CRITICAL LOADING
        accessLevel: book.accessLevel || 'public',
        accessPassword: book.accessPassword || '',
        requireAuth: book.requireAuth || false,
        authorizedUsers: initializeAuthorizedUsers(book.authorizedUsers),
        
        // MANAGEMENT ACCESS - NEW
        managementAccess: book.managementAccess || {
          allOrgUsers: false,
          specificEmails: []
        },
        
        // VIEW ACCESS
          viewAccess: book.viewAccess || {
            enablePublicView: true,
            enableManagementView: true,
            allowViewToggle: true,
            defaultView: 'public',
            requireAuthForManagement: true
          },
          
          // PRIVACY SETTINGS
          allowAnonymousViews: book.allowAnonymousViews !== undefined ? book.allowAnonymousViews : true,
          requireLoginForContact: book.requireLoginForContact || false,
          requireLoginForInteractions: book.albumSettings?.requireLoginForInteractions || true,
          moderateSubmissions: book.moderateSubmissions || false,
          published: book.published !== undefined ? book.published : true,
        
        // SEO & Domain
        customDomain: book.customDomain || '',
        customPath: book.customPath || '',
        seoTitle: book.seoTitle || '',
        seoDescription: book.seoDescription || '',
        seoKeywords: book.seoKeywords || '',

        viewSettings: book.albumSettings?.viewSettings || {
          enablePublicView: true,
          enableManagementView: true,
          publicViewLabel: 'Public View',
          managementViewLabel: 'Portal',
          defaultView: 'public',
          requireAuth: true,
          allowedRoles: ['owner', 'admin', 'faculty', 'coordinator']
        },

        permissions: book.permissions || {},
        authorizedEmails: book.authorizedEmails || [],

        uploadSettings: {
          allowUserUploads: book.uploadSettings?.allowUserUploads || false,
          userUploadMode: book.uploadSettings?.userUploadMode || 'authenticated',
          requireModeration: book.uploadSettings?.userUploadMode === 'authenticated'
        },

        voiceSettings: {
        enabled: book?.voiceSettings?.enabled || false,
        albumNarration: {
          enabled: book?.voiceSettings?.albumNarration?.enabled || false,
          autoPlay: book?.voiceSettings?.albumNarration?.autoPlay || false,
          showTranscript: book?.voiceSettings?.albumNarration?.showTranscript !== false,
          playerPosition: book?.voiceSettings?.albumNarration?.playerPosition || 'top-right'
        },
        galleryNarration: {
          enabled: book?.voiceSettings?.galleryNarration?.enabled || false,
          displayMode: book?.voiceSettings?.galleryNarration?.displayMode || 'banner',
          enableSegments: book?.voiceSettings?.galleryNarration?.enableSegments || false
        },
        mediaVoiceNotes: {
          enabled: book?.voiceSettings?.mediaVoiceNotes?.enabled || false,
          maxDuration: book?.voiceSettings?.mediaVoiceNotes?.maxDuration || 60,
          allowMultipleSpeakers: book?.voiceSettings?.mediaVoiceNotes?.allowMultipleSpeakers !== false,
          autoTranscribe: book?.voiceSettings?.mediaVoiceNotes?.autoTranscribe || false,
          indicatorStyle: book?.voiceSettings?.mediaVoiceNotes?.indicatorStyle || 'waveform'
        },
        permissions: {
          canRecord: book?.voiceSettings?.permissions?.canRecord || 'owner-only',
          requireModeration: book?.voiceSettings?.permissions?.requireModeration || false
        }
      },
      
      // Voice stats
      stats: {
        totalVoiceNotes: book?.stats?.totalVoiceNotes || 0,
        voicePlaybacks: book?.stats?.voicePlaybacks || 0,
        avgListenRate: book?.stats?.avgListenRate || 0
      },
        
        // Advanced Settings
        enableAnalytics: book.albumSettings?.enableAnalytics !== false,
        enableRightClickProtection: book.albumSettings?.enableRightClickProtection || false,
        customCSS: book.customCSS || '',

        enableFlipCards: book?.cardSettings?.enableFlipCards || false,
        flipDirection: book?.cardSettings?.flipDirection || 'horizontal',
        flipDuration: book?.cardSettings?.flipDuration || '0.6s',
        autoFlipOnHover: book?.cardSettings?.autoFlipOnHover || false,
        showFlipIndicator: book?.cardSettings?.showFlipIndicator !== false,
      });

      // Set preview images
      setLogoPreview(book.logoUrl || '');
      setCoverImagePreview(book.coverImageUrl || '');
      setHeaderBackgroundPreview(book.headerBackgroundUrl || '');
      setFaviconPreview(book.favicon || '');
      setOgImagePreview(book.ogImage || '');

      loadAlbumStories();
    }
    
  }, [book]);

  const initializeAuthorizedUsers = (authorizedUsers) => {
    // If no data, return empty object
    if (!authorizedUsers) return {};
    
    // If already in correct format (object), return as is
    if (!Array.isArray(authorizedUsers) && typeof authorizedUsers === 'object') {
      return authorizedUsers;
    }
    
    // If old format (array), migrate to new format
    if (Array.isArray(authorizedUsers)) {
      return authorizedUsers.reduce((acc, email) => {
        acc[email] = {
          email,
          permissions: {
            management: true,
            voiceRecording: true,
            uploads: true
          },
          addedAt: new Date().toISOString()
        };
        return acc;
      }, {});
    }
    
    return {};
  };

  const loadAlbumStories = async () => {
    if (!book?.id) return;
    
    try {
      const stories = await storyService.getAlbumStories(book.id);
      setAlbumStories(stories);
    } catch (error) {
      console.error('Error loading album stories:', error);
    }
  };


  const handleCategoryChange = (newCategory) => {
    const categoryPreset = ALBUM_CATEGORIES[newCategory];
    
    setFormData(prev => ({
      ...prev,
      albumCategory: newCategory,
      // Reset subcategory when changing main category
      albumSubcategory: null,
      // Use defaultFields if they exist, otherwise use empty object
      enabledFields: categoryPreset?.defaultFields || {},
      primaryColor: categoryPreset?.color || '#0795ff'
    }));
  };

  //  Handle subcategory selection
  const handleSubcategoryChange = (subcategoryKey) => {
    const currentCategory = ALBUM_CATEGORIES[formData.albumCategory];
    const subcategoryPreset = currentCategory?.subcategories?.[subcategoryKey];
    
    setFormData(prev => ({
      ...prev,
      albumSubcategory: subcategoryKey,
      // Update enabled fields based on subcategory fields if available
      enabledFields: subcategoryPreset?.fields 
        ? subcategoryPreset.fields.reduce((acc, field) => {
            acc[field] = true;
            return acc;
          }, {})
        : prev.enabledFields,
      primaryColor: subcategoryPreset?.color || prev.primaryColor
    }));
  };

  // Handle field toggle
  const handleFieldToggle = (fieldKey, enabled) => {
    setFormData(prev => ({
      ...prev,
      enabledFields: {
        ...prev.enabledFields,
        [fieldKey]: enabled
      }
    }));
  };

  // Handle field settings
  const handleFieldSetting = (fieldKey, setting, value) => {
    setFormData(prev => ({
      ...prev,
      fieldSettings: {
        ...prev.fieldSettings,
        [fieldKey]: {
          ...prev.fieldSettings[fieldKey],
          [setting]: value
        }
      }
    }));
  };

  // File upload handlers
  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setLogoPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCoverImageSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setCoverImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setCoverImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleHeaderBackgroundSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setHeaderBackgroundFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setHeaderBackgroundPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleFaviconSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFaviconFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setFaviconPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleOgImageSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setOgImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setOgImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  // Upload all files to Firebase Storage
  const uploadFiles = async () => {
    setUploading(true);
    setUploadProgress(0);
    
    try {
      const uploads = [];
      const uploadedUrls = {};
      
      if (logoFile) {
        uploads.push({
          file: logoFile,
          path: 'logo',
          field: 'logoUrl'
        });
      }
      
      if (coverImageFile) {
        uploads.push({
          file: coverImageFile,
          path: 'cover',
          field: 'coverImageUrl'
        });
      }
      
      if (headerBackgroundFile) {
        uploads.push({
          file: headerBackgroundFile,
          path: 'header-bg',
          field: 'headerBackgroundUrl'
        });
      }

      if (ogImageFile) {
        uploads.push({
          file: ogImageFile,
          path: 'og-image',
          field: 'ogImage'
        });
      }

      if (faviconFile) {
        uploads.push({
          file: faviconFile,
          path: 'favicon',
          field: 'favicon'
        });
      }

      if (uploads.length === 0) return {};

      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        const result = await albumService.uploadFile(book.id, upload.file, upload.path);
        
        // Store the URL in our return object
        uploadedUrls[upload.field] = result.url;
        
        // Update form data for UI preview
        setFormData(prev => ({
          ...prev,
          [upload.field]: result.url
        }));
        
        setUploadProgress(Math.round(((i + 1) / uploads.length) * 100));
      }

      // Return the uploaded URLs
      return uploadedUrls;
      
    } catch (error) {
      console.error('Error uploading files:', error);
      throw error;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };
  

  const handleSave = async () => {
    if (!book) return;
    
    setSaving(true);
    try {
      // Upload files first and get the URLs
      const uploadedUrls = await uploadFiles();

      const sanitizedQuickStatusOptions = formData.quickStatusOptions 
      ? Object.fromEntries(
          Object.entries(formData.quickStatusOptions).map(([key, config]) => [
            key,
            {
              enabled: config.enabled ?? false,
              showOnCard: config.showOnCard ?? true,
              trackHistory: config.trackHistory ?? true,
              requireNotes: config.requireNotes ?? false,
              label: config.label ?? '',
              description: config.description ?? '',
              color: config.color ?? '#000000',
              options: config.options ?? []
              // 'icon' field is intentionally excluded
            }
          ])
        )
      : {};
      
      // Create updated book object with uploaded URLs
      const updatedBook = {
        ...book,
        // Basic info
        title: formData.botName || formData.title,
        botName: formData.botName || formData.title,
        description: formData.description,
        
        // Use uploaded URLs if available, otherwise use formData
        coverImageUrl: uploadedUrls.coverImageUrl || formData.coverImageUrl,
        logoUrl: uploadedUrls.logoUrl || formData.logoUrl,
        headerBackgroundUrl: uploadedUrls.headerBackgroundUrl || formData.headerBackgroundUrl,
        favicon: uploadedUrls.favicon || formData.favicon || book.favicon || '',
        ogImage: uploadedUrls.ogImage || formData.ogImage || book.ogImage || '',
        
        // ACCESS LEVEL - CRITICAL FIELDS
        accessLevel: formData.accessLevel || 'public',
        accessPassword: formData.accessPassword || '',
        requireAuth: formData.requireAuth === true,
        authorizedUsers: formData.authorizedUsers || {},
        
        // MANAGEMENT ACCESS - NEW CRITICAL FIELDS
        managementAccess: {
          allOrgUsers: formData.managementAccess?.allOrgUsers || false,
          specificEmails: formData.managementAccess?.specificEmails || []
        },
        
        // VIEW ACCESS
        viewAccess: {
          enablePublicView: formData.viewAccess?.enablePublicView !== false,
          enableManagementView: formData.viewAccess?.enableManagementView !== false,
          allowViewToggle: formData.viewAccess?.allowViewToggle !== false,
          defaultView: formData.viewAccess?.defaultView || 'public',
          requireAuthForManagement: formData.viewAccess?.requireAuthForManagement !== false
        },
        
        // PRIVACY SETTINGS
        allowAnonymousViews: formData.allowAnonymousViews !== false,
        requireLoginForContact: formData.requireLoginForContact === true,
        moderateSubmissions: formData.moderateSubmissions === true,
        published: formData.published !== false,
        
        // Organization link
        organizationId: formData.organizationId || null,
        
        // SEO
        seoTitle: formData.seoTitle,
        seoDescription: formData.seoDescription,
        seoKeywords: formData.seoKeywords,
        // SEO & Domain
        customDomain: formData.customDomain,
        customPath: formData.customPath,
        
        // Interaction settings at top level
        allowLikes: formData.allowLikes,
        allowComments: formData.allowComments,
        allowSharing: formData.allowSharing,
        allowDownloads: formData.allowDownloads,
        allowShopping: formData.allowShopping,    
        allowBooking: formData.allowBooking,
        showViewCounts: formData.showViewCounts,

        transactions: formData.transactions,
        
        // Custom CSS
        customCSS: formData.customCSS,
        
        // Custom styling object
        styling: {
          primaryColor: formData.primaryColor,
          backgroundColor: formData.backgroundColor,
          headerColor: formData.headerColor,
          headerTextColor: formData.headerTextColor,
          headerOpacity: formData.headerOpacity,
          textColor: formData.textColor,
          accentColor: formData.accentColor,
          fontFamily: formData.fontFamily,
          headerRadius: formData.headerRadius,
          cardRadius: formData.cardRadius,
          shadowLevel: formData.shadowLevel,
          logoInvert: formData.logoInvert,
          gallerySidebarBg: formData.gallerySidebarBg,
          gallerySidebarShadow: formData.gallerySidebarShadow
        },
        
        // All album-specific settings
        albumSettings: {
          albumCategory: formData.albumCategory,
          albumSubcategory: formData.albumSubcategory,
          enabledFields: formData.enabledFields,
          fieldSettings: formData.fieldSettings,

          headerSettings: formData.headerSettings, 
          
          viewSettings: formData.viewSettings,
          permissions: formData.permissions,
          authorizedEmails: formData.authorizedEmails,

          quickStatusOptions: sanitizedQuickStatusOptions || {}, 
          teamRecordCategories: formData.teamRecordCategories || [], 
          wellbeingAlerts: formData.wellbeingAlerts || {},

          sidestrip: {
            enabled: formData.sidestrip?.enabled || false,
            position: formData.sidestrip?.position || 'right',
            showThumbnails: formData.sidestrip?.showThumbnails !== false,
            showLabels: formData.sidestrip?.showLabels !== false,
            showCounts: formData.sidestrip?.showCounts || false,
            collapsible: formData.sidestrip?.collapsible !== false,
            width: formData.sidestrip?.width || 'medium',
            thumbnailSize: formData.sidestrip?.thumbnailSize || 'medium'
          },

          defaultView: formData.defaultView,
          columnsBreakpoints: formData.columnsBreakpoints,
          cardStyle: formData.cardStyle,
          showMediaTitles: formData.showMediaTitles,
          showMediaDescriptions: formData.showMediaDescriptions,
          showTags: formData.showTags,
          showInteractionStats: formData.showInteractionStats,
          autoCreateFirstGallery: formData.autoCreateFirstGallery,
          defaultGalleryName: formData.defaultGalleryName,
          showGalleryTabs: formData.showGalleryTabs,
          allowMultipleGalleries: formData.allowMultipleGalleries,
          maxMediaSize: formData.maxMediaSize,
          autoGenerateThumbnails: formData.autoGenerateThumbnails,
          enableImageOptimization: formData.enableImageOptimization,
          allowedFileTypes: formData.allowedFileTypes,
          requireLoginForInteractions: formData.requireLoginForInteractions,
          enableAnalytics: formData.enableAnalytics,
          enableRightClickProtection: formData.enableRightClickProtection
          
        },

        uploadSettings: {
          allowUserUploads: formData.uploadSettings.allowUserUploads,
          userUploadMode: formData.uploadSettings.userUploadMode,
          requireModeration: formData.uploadSettings.userUploadMode === 'authenticated'
        },

        voiceSettings: {
          enabled: formData.voiceSettings?.enabled || false,
          
          // Album-level narration with permissions
          albumNarration: {
            enabled: formData.voiceSettings?.albumNarration?.enabled || false,
            autoPlay: formData.voiceSettings?.albumNarration?.autoPlay || false,
            showTranscript: formData.voiceSettings?.albumNarration?.showTranscript !== false,
            playerPosition: formData.voiceSettings?.albumNarration?.playerPosition || 'top-right',
            permissions: {
              canRecord: formData.voiceSettings?.albumNarration?.permissions?.canRecord || 'owner-only',
              requireModeration: formData.voiceSettings?.albumNarration?.permissions?.requireModeration || false
            }
          },
          
          // Gallery-level narration with permissions
          galleryNarration: {
            enabled: formData.voiceSettings?.galleryNarration?.enabled || false,
            displayMode: formData.voiceSettings?.galleryNarration?.displayMode || 'banner',
            enableSegments: formData.voiceSettings?.galleryNarration?.enableSegments || false,
            permissions: {
              canRecord: formData.voiceSettings?.galleryNarration?.permissions?.canRecord || 'owner-only',
              requireModeration: formData.voiceSettings?.galleryNarration?.permissions?.requireModeration || false
            }
          },
          
          // Media-level voice notes with permissions
          mediaVoiceNotes: {
            enabled: formData.voiceSettings?.mediaVoiceNotes?.enabled || false,
            maxDuration: formData.voiceSettings?.mediaVoiceNotes?.maxDuration || 60,
            allowMultipleSpeakers: formData.voiceSettings?.mediaVoiceNotes?.allowMultipleSpeakers !== false,
            autoTranscribe: formData.voiceSettings?.mediaVoiceNotes?.autoTranscribe || false,
            indicatorStyle: formData.voiceSettings?.mediaVoiceNotes?.indicatorStyle || 'waveform',
            permissions: {
              canRecord: formData.voiceSettings?.mediaVoiceNotes?.permissions?.canRecord || 'authenticated',
              requireModeration: formData.voiceSettings?.mediaVoiceNotes?.permissions?.requireModeration || false
            }
          }
        },
    
        cardSettings: {
        enableFlipCards: formData.enableFlipCards,
        flipDirection: formData.flipDirection,
        flipDuration:formData.flipDuration,
        autoFlipOnHover: formData.autoFlipOnHover,
        showFlipIndicator: formData.showFlipIndicator,

        },

        updatedAt: new Date()
      };

      await onUpdate(updatedBook);
      
      // Clear file states after successful save
      setLogoFile(null);
      setCoverImageFile(null);
      setHeaderBackgroundFile(null);
      setFaviconFile(null);
      setOgImageFile(null);
      
      onClose();
    } catch (error) {
      console.error('Error saving album settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const validateAuthorizedUsers = (authorizedUsers) => {
    if (!authorizedUsers || typeof authorizedUsers !== 'object') return false;
    
    // Check if object structure is correct
    for (const [email, userData] of Object.entries(authorizedUsers)) {
      if (!userData.email || !userData.permissions || !userData.addedAt) {
        console.error('Invalid user data structure for:', email);
        return false;
      }
      
      if (typeof userData.permissions !== 'object') {
        console.error('Invalid permissions structure for:', email);
        return false;
      }
    }
    
    return true;
  };

  const handleInputChange = (path, value) => {
    console.log('Input change:', path, value); // Debug log
    
    setFormData(prev => {
      // Handle nested paths with dot notation
      if (path.includes('.')) {
        const keys = path.split('.');
        const newData = { ...prev };
        
        // Navigate to parent object
        let current = newData;
        for (let i = 0; i < keys.length - 1; i++) {
          const key = keys[i];
          
          // Ensure nested object exists
          if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
          } else {
            current[key] = { ...current[key] };
          }
          
          current = current[key];
        }
        
        // Set the final value
        current[keys[keys.length - 1]] = value;
        
        return newData;
      }
      
      // Handle simple top-level properties
      return {
        ...prev,
        [path]: value
      };
    });
  };

  const handleNestedInputChange = (parentKey, childKey, value) => {
    setFormData(prev => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [childKey]: value
      }
    }));
  };

  const handleColumnsChange = (breakpoint, value) => {
    const numValue = parseInt(value);
    if (numValue >= 1 && numValue <= 8) {
      setFormData(prev => ({
        ...prev,
        columnsBreakpoints: {
          ...prev.columnsBreakpoints,
          [breakpoint]: numValue
        }
      }));
    }
  };

  // Enhanced validation
  const validateBreakpoints = (breakpoints) => {
    const validated = {};
    const defaultBreakpoints = { mobile: 1, tablet: 2, desktop: 3, wide: 4 };
    
    Object.entries(defaultBreakpoints).forEach(([key, fallback]) => {
      const value = breakpoints[key];
      validated[key] = (typeof value === 'number' && value >= 1 && value <= 8) 
        ? value 
        : fallback;
    });
    
    return validated;
  };

  const generatePreviewUrl = () => {
    const domain = formData.customDomain.trim();
    const path = formData.customPath.trim();
    
    if (!domain) return '';
    
    if (path) {
      return `https://${domain}/${path}`;
    }
    return `https://${domain}`;
  };

  const previewUrl = generatePreviewUrl();

  // Preview styles for live preview
  const previewStyles = {
    '--album-primary': formData.primaryColor,
    '--album-background': formData.backgroundColor,
    '--album-header': formData.headerColor,
    '--album-header-text': formData.headerTextColor,
    '--album-accent': formData.accentColor,
    '--album-header-radius': formData.headerRadius,
    '--album-card-radius': formData.cardRadius,
    fontFamily: formData.fontFamily
  };

  const getShadowClass = () => {
    switch (formData.shadowLevel) {
      case 'none': return 'shadow-none';
      case 'small': return 'shadow-sm';
      case 'medium': return 'shadow-md';
      case 'large': return 'shadow-lg';
      case 'extra-large': return 'shadow-xl';
      default: return 'shadow-md';
    }
  };

  if (!isOpen) return null;

  const currentCategory = ALBUM_CATEGORIES[formData.albumCategory];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Album Settings
            {currentCategory && (
              <span className="ml-2 px-3 py-1 rounded-full text-sm" style={{ 
                backgroundColor: currentCategory.color + '20', 
                color: currentCategory.color 
              }}>
                {currentCategory.label}
              </span>
            )}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar Navigation */}
          <div className="w-64 border-r bg-gray-50 p-4 overflow-y-auto">
            <nav className="space-y-2">
              {[
                { id: 'category', label: 'Album Category', icon: Grid },
                { id: 'fields', label: 'Media Fields', icon: Type },
                { id: 'basic', label: 'Basic Info', icon: Settings },
                { id: 'header', label: 'Header', icon: Layout },
                { id: 'display', label: 'Display', icon: Eye },
                { 
                  id: 'team', 
                  label: 'Team Settings', 
                  icon: Users,
                  badge: formData.albumCategory === 'teams' && 
                        Object.values(formData.quickStatusOptions || {}).filter(s => s.enabled).length > 0
                },
                { id: 'voice', label: 'Voice & Audio', icon: Mic },
                { id: 'view', label: 'View Settings', icon: Globe },
                { id: 'design', label: 'Brand Design', icon: Palette },
                { id: 'discovery', label: 'Discovery', icon: Compass },
                { id: 'interactions', label: 'Interactions', icon: Heart },
                { id: 'transactions', label: 'Transactions', icon: DollarSign },
                { id: 'images', label: 'Media Settings', icon: ImageIcon },
                { id: 'access', label: 'Access & Privacy', icon: Lock },
                { id: 'seo', label: 'Domain & SEO', icon: Globe },
                { id: 'advanced', label: 'Advanced', icon: Settings },
                { id: 'preview', label: 'Preview', icon: Eye }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Album Category Tab */}
            {activeTab === 'category' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Album Type</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Select the type of album you're creating. This will configure the appropriate fields and layout for your content.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(ALBUM_CATEGORIES).map(([key, category]) => {
                    const IconComponent = category.icon;
                    const isSelected = formData.albumCategory === key;
                    const hasSubcategories = category.subcategories;
                    
                    return (
                      <div key={key} className="space-y-3">
                        {/* Main Category Card */}
                        <button
                          onClick={() => handleCategoryChange(key)}
                          className={`w-full p-6 rounded-xl border-2 text-left transition-all hover:shadow-md ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <div 
                              className="p-3 rounded-lg"
                              style={{ 
                                backgroundColor: category.color + '20',
                                color: category.color 
                              }}
                            >
                              {IconComponent && <IconComponent className="h-6 w-6" />}
                            </div>
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{category.label}</h4>
                              {isSelected && (
                                <div className="flex items-center gap-1 text-sm text-blue-600">
                                  <Check className="h-4 w-4" />
                                  Selected
                                </div>
                              )}
                            </div>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{category.description}</p>
                          
                          {/* Show default fields preview for simple categories */}
                          {!hasSubcategories && category.defaultFields && (
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(category.defaultFields)
                                .filter(([_, enabled]) => enabled)
                                .slice(0, 4)
                                .map(([field, _]) => (
                                  <span 
                                    key={field}
                                    className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                                  >
                                    {MEDIA_CARD_FIELDS[field]?.label || field}
                                  </span>
                                ))}
                              {Object.values(category.defaultFields).filter(Boolean).length > 4 && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                  +{Object.values(category.defaultFields).filter(Boolean).length - 4} more
                                </span>
                              )}
                            </div>
                          )}

                          {/* Show subcategories indicator */}
                          {hasSubcategories && (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <Layers className="h-4 w-4" />
                              <span>{Object.keys(category.subcategories).length} subcategories</span>
                            </div>
                          )}
                        </button>

                        {/* Subcategories - only show if category is selected and has subcategories */}
                        {isSelected && hasSubcategories && (
                          <div className="ml-4 space-y-2 border-l-2 border-blue-200 pl-4">
                            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                              <Layers className="h-3 w-3" />
                              Select Subcategory
                            </p>
                            <div className="space-y-2">
                              {Object.entries(category.subcategories).map(([subKey, subcategory]) => {
                                const SubIcon = subcategory.icon;
                                const isSubSelected = formData.albumSubcategory === subKey;
                                
                                return (
                                  <button
                                    key={subKey}
                                    onClick={() => handleSubcategoryChange(subKey)}
                                    className={`w-full p-3 rounded-lg border-2 text-left transition-all hover:shadow-sm ${
                                      isSubSelected
                                        ? 'border-blue-400 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300 bg-white'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3 mb-2">
                                      {SubIcon && (
                                        <SubIcon 
                                          className="h-4 w-4" 
                                          style={{ color: subcategory.color }}
                                        />
                                      )}
                                      <span className="text-sm font-medium text-gray-900 flex-1">
                                        {subcategory.label}
                                      </span>
                                      {isSubSelected && (
                                        <Check className="h-4 w-4 text-blue-600" />
                                      )}
                                    </div>
                                    
                                    {/* Show field preview */}
                                    {subcategory.fields && (
                                      <div className="flex flex-wrap gap-1 mt-2">
                                        {subcategory.fields.slice(0, 4).map((field) => (
                                          <span 
                                            key={field}
                                            className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
                                          >
                                            {MEDIA_CARD_FIELDS[field]?.label || field}
                                          </span>
                                        ))}
                                        {subcategory.fields.length > 4 && (
                                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                                            +{subcategory.fields.length - 4} more
                                          </span>
                                        )}
                                      </div>
                                    )}

                                    {/* Show interactions if available */}
                                    {subcategory.interactions && (
                                      <div className="flex items-center gap-1 mt-2 text-xs text-gray-500">
                                        <Heart className="h-3 w-3" />
                                        <span>{subcategory.interactions.length} interactions</span>
                                      </div>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Show selected configuration summary */}
                {formData.albumCategory && (
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                      <Info className="h-4 w-4" />
                      Current Configuration
                    </h4>
                    <div className="text-sm text-blue-800 space-y-1">
                      <p>
                        <strong>Category:</strong> {ALBUM_CATEGORIES[formData.albumCategory]?.label}
                      </p>
                      {formData.albumSubcategory && ALBUM_CATEGORIES[formData.albumCategory]?.subcategories?.[formData.albumSubcategory] && (
                        <p>
                          <strong>Subcategory:</strong> {ALBUM_CATEGORIES[formData.albumCategory].subcategories[formData.albumSubcategory].label}
                        </p>
                      )}
                      <p>
                        <strong>Enabled Fields:</strong> {Object.values(formData.enabledFields || {}).filter(Boolean).length} fields
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Media Fields Tab */}
            {activeTab === 'fields' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Configure Media Card Fields</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Choose which fields appear on your media cards. Fields will be displayed below the media image/video.
                  </p>
                </div>

                {/* Preview of selected category */}
                {currentCategory && (
                  <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-3 mb-2">
                      <currentCategory.icon 
                        className="h-5 w-5" 
                        style={{ color: currentCategory.color }} 
                      />
                      <span className="font-medium text-gray-900">
                        {currentCategory.label} Default Fields
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{currentCategory.description}</p>
                  </div>
                )}

                {/* Field Groups */}
                <div className="space-y-8">
                  {/* Basic Information Fields */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Basic Information</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(MEDIA_CARD_FIELDS)
                        .filter(([_, field]) => ['title', 'description', 'tags', 'mobile', 'email'].includes(_))
                        .map(([key, field]) => {
                          const IconComponent = field.icon;
                          const isEnabled = formData.enabledFields[key];
                          
                          return (
                            <label 
                              key={key}
                              className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                isEnabled 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isEnabled || false}
                                onChange={(e) => handleFieldToggle(key, e.target.checked)}
                                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <IconComponent className="h-4 w-4 text-gray-500" />
                                  <span className="font-medium text-gray-900">{field.label}</span>
                                </div>
                                <div className="text-sm text-gray-600">
                                  Type: {field.type}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                  </div>

                  {/* Commercial Fields */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-4">Commercial Fields</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Object.entries(MEDIA_CARD_FIELDS)
                        .filter(([_, field]) => ['price', 'brand', 'brandLogo', 'quantity', 'sku', 'rating', 'link'].includes(_))
                        .map(([key, field]) => {
                          const IconComponent = field.icon;
                          const isEnabled = formData.enabledFields[key];
                          
                          return (
                            <label 
                              key={key}
                              className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                isEnabled 
                                  ? 'border-blue-500 bg-blue-50' 
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isEnabled || false}
                                onChange={(e) => handleFieldToggle(key, e.target.checked)}
                                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <IconComponent className="h-4 w-4 text-gray-500" />
                                  <span className="font-medium text-gray-900">{field.label}</span>
                                </div>
                                <div className="text-sm text-gray-600">
                                  Type: {field.type}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                    </div>
                  </div>

                  {/* Category-Specific Fields */}
                  {formData.albumCategory === 'menu' && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-4">Menu-Specific Fields</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(MEDIA_CARD_FIELDS)
                          .filter(([_, field]) => ['category', 'allergens', 'spiceLevel', 'calories', 'availability'].includes(_))
                          .map(([key, field]) => {
                            const IconComponent = field.icon;
                            const isEnabled = formData.enabledFields[key];
                            
                            return (
                              <label 
                                key={key}
                                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                  isEnabled 
                                    ? 'border-blue-500 bg-blue-50' 
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isEnabled || false}
                                  onChange={(e) => handleFieldToggle(key, e.target.checked)}
                                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <IconComponent className="h-4 w-4 text-gray-500" />
                                    <span className="font-medium text-gray-900">{field.label}</span>
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Type: {field.type}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Location & Contact Fields */}
                  {['listing', 'store'].includes(formData.albumCategory) && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-4">Location & Contact</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(MEDIA_CARD_FIELDS)
                          .filter(([_, field]) => ['location', 'contact', 'features', 'size'].includes(_))
                          .map(([key, field]) => {
                            const IconComponent = field.icon;
                            const isEnabled = formData.enabledFields[key];
                            
                            return (
                              <label 
                                key={key}
                                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                  isEnabled 
                                    ? 'border-blue-500 bg-blue-50' 
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isEnabled || false}
                                  onChange={(e) => handleFieldToggle(key, e.target.checked)}
                                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <IconComponent className="h-4 w-4 text-gray-500" />
                                    <span className="font-medium text-gray-900">{field.label}</span>
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Type: {field.type}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Portfolio Fields */}
                  {formData.albumCategory === 'showcase' && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-4">Portfolio/Showcase Fields</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {Object.entries(MEDIA_CARD_FIELDS)
                          .filter(([_, field]) => ['client', 'projectDate', 'skills', 'awards'].includes(_))
                          .map(([key, field]) => {
                            const IconComponent = field.icon;
                            const isEnabled = formData.enabledFields[key];
                            
                            return (
                              <label 
                                key={key}
                                className={`flex items-start gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                                  isEnabled 
                                    ? 'border-blue-500 bg-blue-50' 
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isEnabled || false}
                                  onChange={(e) => handleFieldToggle(key, e.target.checked)}
                                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <IconComponent className="h-4 w-4 text-gray-500" />
                                    <span className="font-medium text-gray-900">{field.label}</span>
                                  </div>
                                  <div className="text-sm text-gray-600">
                                    Type: {field.type}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>

                {/* Field Configuration Summary */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-2">Selected Fields Summary</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(formData.enabledFields)
                      .filter(([_, enabled]) => enabled)
                      .map(([key, _]) => (
                        <span 
                          key={key}
                          className="px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full"
                        >
                          {MEDIA_CARD_FIELDS[key]?.label || key}
                        </span>
                      ))}
                    {Object.values(formData.enabledFields).filter(Boolean).length === 0 && (
                      <span className="text-gray-500 text-sm">No fields selected</span>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'basic' && (
              <BasicInfoTab
                formData={formData}
                onInputChange={handleInputChange}
                onImageUpload={uploadFiles}
              />
            )}

            {activeTab === 'header' && (
              <HeaderSettings
                formData={formData}
                onInputChange={handleInputChange}
                
                onImageUpload={uploadFiles}
              />
            )}

            {activeTab === 'view' && (
              <ViewSettings
                formData={formData}
                onInputChange={handleInputChange}
                templateType="institution"
              />
            )}  

            {activeTab === 'design' && (
              <DesignBrandingSection
                formData={formData}
                onInputChange={handleInputChange}
                templateType="album"
              />
            )}      

            {activeTab === 'team' && (
              <TeamSettingsTab
                formData={formData}
                onInputChange={handleInputChange}
              />
            )}      

            {/* Show message if team tab accessed but not teams category */}
            {activeTab === 'team' && formData.albumCategory !== 'teams' && (
              <div className="flex flex-col items-center justify-center py-12">
                <Users className="h-16 w-16 text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Team Settings Not Available
                </h3>
                <p className="text-sm text-gray-600 text-center max-w-md mb-4">
                  Team settings are only available when your album category is set to "Team Management".
                </p>
                <button
                  onClick={() => {
                    handleCategoryChange('teams');
                    setActiveTab('category');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Switch to Team Management
                </button>
              </div>
            )}

            {/*
            {activeTab === 'styling' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Colors & Styling</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Color Controls 
                    <div className="space-y-4">
                      {[
                        { key: 'primaryColor', label: 'Primary Color', desc: 'Main brand color' },
                        { key: 'backgroundColor', label: 'Background Color', desc: 'Page background' },
                        { key: 'headerColor', label: 'Header Background', desc: 'Header background color' },
                        { key: 'headerTextColor', label: 'Header Text Color', desc: 'Header text and icons' },
                        { key: 'accentColor', label: 'Accent Color', desc: 'Buttons and highlights' }
                      ].map(({ key, label, desc }) => (
                        <div key={key}>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {label}
                          </label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={formData[key]}
                              onChange={(e) => handleInputChange(key, e.target.value)}
                              className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
                            />
                            <input
                              type="text"
                              value={formData[key]}
                              onChange={(e) => handleInputChange(key, e.target.value)}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">{desc}</p>
                        </div>
                      ))}
                    </div>

                    {/* Style Controls 
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Font Family
                        </label>
                        <select
                          value={formData.fontFamily}
                          onChange={(e) => handleInputChange('fontFamily', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="Inter, sans-serif">Inter (Modern)</option>
                          <option value="system-ui, sans-serif">System UI</option>
                          <option value="'Helvetica Neue', Arial, sans-serif">Helvetica Neue</option>
                          <option value="'Georgia', serif">Georgia (Serif)</option>
                          <option value="'Courier New', monospace">Courier New (Mono)</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Header Border Radius
                        </label>
                        <select
                          value={formData.headerRadius}
                          onChange={(e) => handleInputChange('headerRadius', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="0px">None (Sharp)</option>
                          <option value="8px">Small</option>
                          <option value="12px">Medium</option>
                          <option value="16px">Large</option>
                          <option value="24px">Extra Large</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Card Border Radius
                        </label>
                        <select
                          value={formData.cardRadius}
                          onChange={(e) => handleInputChange('cardRadius', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="0px">None (Sharp)</option>
                          <option value="8px">Small</option>
                          <option value="12px">Medium</option>
                          <option value="16px">Large</option>
                          <option value="24px">Extra Large</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Shadow Level
                        </label>
                        <select
                          value={formData.shadowLevel}
                          onChange={(e) => handleInputChange('shadowLevel', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="none">No Shadow</option>
                          <option value="small">Small</option>
                          <option value="medium">Medium</option>
                          <option value="large">Large</option>
                          <option value="extra-large">Extra Large</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            */}

            {activeTab === 'stories' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Story Display Settings</h3>
                </div>

                {/* Gallery Story Display Mode */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Gallery Story Display
                  </label>
                  <div className="grid grid-cols-3 gap-4">
                    {[
                      { id: 'cards', label: 'Story Cards', desc: 'Individual story cards in gallery' },
                      { id: 'carousel', label: 'Carousel', desc: 'Carousel at top of album' },
                      { id: 'hidden', label: 'Hidden', desc: 'Don\'t show gallery stories' }
                    ].map(mode => (
                      <button
                        key={mode.id}
                        onClick={() => handleInputChange('storyDisplayMode', mode.id)}
                        className={`p-4 border-2 rounded-lg text-left ${
                          formData.storyDisplayMode === mode.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-300'
                        }`}
                      >
                        <div className="font-medium">{mode.label}</div>
                        <div className="text-sm text-gray-500">{mode.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Default View Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Default Album View
                  </label>
                  <select
                    value={formData.defaultView || 'galleries'}
                    onChange={(e) => handleInputChange('defaultView', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="galleries">Gallery View</option>
                    {albumStories.filter(s => s.level === 'album').map(story => (
                      <option key={story.id} value={story.id}>
                        Story: {story.title}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Story Border Color for Gallery Stories */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Gallery Story Border Color
                  </label>
                  <input
                    type="color"
                    value={formData.galleryStoryBorder || '#8b5cf6'}
                    onChange={(e) => handleInputChange('galleryStoryBorder', e.target.value)}
                    className="w-full h-10 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
            )}

            {activeTab === 'images' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Images & Media</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {/* Logo Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Logo
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        {logoPreview ? (
                          <div className="relative">
                            <img
                              src={logoPreview}
                              alt="Logo Preview"
                              className="w-full h-24 object-contain rounded-lg bg-gray-50"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setLogoPreview('');
                                setLogoFile(null);
                                handleInputChange('logoUrl', '');
                              }}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-2">
                              <label htmlFor="logo-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                <Upload className="h-4 w-4 inline mr-2" />
                                Upload Logo
                              </label>
                              <input
                                id="logo-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleLogoSelect}
                                className="hidden"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Cover Image Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Cover Image
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        {coverImagePreview ? (
                          <div className="relative">
                            <img
                              src={coverImagePreview}
                              alt="Cover Preview"
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setCoverImagePreview('');
                                setCoverImageFile(null);
                                handleInputChange('coverImageUrl', '');
                              }}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-2">
                              <label htmlFor="cover-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                <Upload className="h-4 w-4 inline mr-2" />
                                Upload Cover
                              </label>
                              <input
                                id="cover-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleCoverImageSelect}
                                className="hidden"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Header Background Upload */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Header Background
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                        {headerBackgroundPreview ? (
                          <div className="relative">
                            <img
                              src={headerBackgroundPreview}
                              alt="Header Background Preview"
                              className="w-full h-24 object-cover rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setHeaderBackgroundPreview('');
                                setHeaderBackgroundFile(null);
                                handleInputChange('headerBackgroundUrl', '');
                              }}
                              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div>
                            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-2">
                              <label htmlFor="header-bg-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                <Upload className="h-4 w-4 inline mr-2" />
                                Upload Background
                              </label>
                              <input
                                id="header-bg-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleHeaderBackgroundSelect}
                                className="hidden"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Media Settings */}
                <div>
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Media Upload Settings</h4>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Maximum File Size (MB)
                      </label>
                      <select
                        value={formData.maxMediaSize}
                        onChange={(e) => handleInputChange('maxMediaSize', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value={5}>5 MB</option>
                        <option value={10}>10 MB</option>
                        <option value={25}>25 MB</option>
                        <option value={50}>50 MB</option>
                        <option value={100}>100 MB</option>
                      </select>
                    </div>

                    <div className="space-y-3">
                      {[
                        { key: 'autoGenerateThumbnails', label: 'Auto-generate Thumbnails', desc: 'Create thumbnails for videos and large images' },
                        { key: 'enableImageOptimization', label: 'Image Optimization', desc: 'Compress images for faster loading' }
                      ].map(({ key, label, desc }) => (
                        <label key={key} className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => handleInputChange(key, e.target.checked)}
                            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'display' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Display Options</h3>
                  
                  <div className="space-y-6">
                    {/* Default View */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Default View
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { value: 'masonry', label: 'Masonry', icon: Grid },
                          { value: 'list', label: 'List', icon: List }
                        ].map(({ value, label, icon: Icon }) => (
                          <label key={value} className={`cursor-pointer border-2 rounded-lg p-4 transition-all ${
                            formData.defaultView === value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                          }`}>
                            <input
                              type="radio"
                              name="defaultView"
                              value={value}
                              checked={formData.defaultView === value}
                              onChange={(e) => handleInputChange('defaultView', e.target.value)}
                              className="sr-only"
                            />
                            <div className="text-center">
                              <Icon className="h-8 w-8 mx-auto mb-2 text-gray-600" />
                              <div className="font-medium text-gray-900">{label}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Column Breakpoints */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Columns per Screen Size
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                          { key: 'mobile', label: 'Mobile', icon: Smartphone, max: 2 },
                          { key: 'tablet', label: 'Tablet', icon: Tablet, max: 4 },
                          { key: 'desktop', label: 'Desktop', icon: Monitor, max: 6 },
                          { key: 'wide', label: 'Wide Screen', icon: Monitor, max: 8 }
                        ].map(({ key, label, icon: Icon, max }) => (
                          <div key={key}>
                            <label className="block text-xs font-medium text-gray-600 mb-2 flex items-center gap-1">
                              <Icon className="h-3 w-3" />
                              {label}
                            </label>
                            <select
                              value={formData.columnsBreakpoints[key]}
                              onChange={(e) => handleColumnsChange(key, e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              {Array.from({ length: max }, (_, i) => i + 1).map(num => (
                                <option key={num} value={num}>{num}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Card Style */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Card Style
                      </label>
                      <select
                        value={formData.cardStyle}
                        onChange={(e) => handleInputChange('cardStyle', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="modern">Modern</option>
                        <option value="classic">Classic</option>
                        <option value="minimal">Minimal</option>
                        <option value="bold">Bold</option>
                      </select>
                    </div>

                    {/* Display Options */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Show Elements
                      </label>
                      <div className="space-y-3">
                        {[
                          { key: 'showMediaTitles', label: 'Media Titles', desc: 'Show titles below media items' },
                          { key: 'showMediaDescriptions', label: 'Media Descriptions', desc: 'Show descriptions below media items' },
                          { key: 'showTags', label: 'Tags', desc: 'Show tags on media items' },
                          { key: 'showInteractionStats', label: 'Interaction Stats', desc: 'Show likes, views, and comment counts' }
                        ].map(({ key, label, desc }) => (
                          <label key={key} className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={formData[key]}
                              onChange={(e) => handleInputChange(key, e.target.checked)}
                              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                              <div className="font-medium text-gray-900">{label}</div>
                              <div className="text-sm text-gray-500">{desc}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Sidestrip Configuration */}
                      <div className="border-t pt-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <PanelRightClose className="h-5 w-5 text-blue-600" />
                          Gallery Sidestrip
                        </h4>
                        
                        <div className="space-y-4">
                          {/* Enable Sidestrip */}
                          <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                            <input
                              type="checkbox"
                              checked={formData.sidestrip?.enabled || false}
                              onChange={(e) => handleInputChange('sidestrip', {
                                ...formData.sidestrip,
                                enabled: e.target.checked
                              })}
                              className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <div className="font-medium text-gray-900">Enable Sidestrip</div>
                              <div className="text-sm text-gray-500">
                                Show a sidebar with quick gallery navigation thumbnails
                              </div>
                            </div>
                          </label>

                          {formData.sidestrip?.enabled && (
                            <>
                              {/* Position Selection */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                  Sidestrip Position
                                </label>
                                <div className="grid grid-cols-2 gap-3">
                                  {[
                                    { value: 'left', label: 'Left Side', icon: PanelLeft },
                                    { value: 'right', label: 'Right Side', icon: PanelRight }
                                  ].map(({ value, label, icon: Icon }) => (
                                    <button
                                      key={value}
                                      onClick={() => handleInputChange('sidestrip', {
                                        ...formData.sidestrip,
                                        position: value
                                      })}
                                      className={`flex items-center justify-center gap-2 p-4 border-2 rounded-lg transition-all ${
                                        formData.sidestrip?.position === value
                                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                                          : 'border-gray-300 hover:border-gray-400'
                                      }`}
                                    >
                                      <Icon className="h-5 w-5" />
                                      <span className="font-medium">{label}</span>
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Display Options */}
                              <div className="space-y-3">
                                <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                  <input
                                    type="checkbox"
                                    checked={formData.sidestrip?.showThumbnails !== false}
                                    onChange={(e) => handleInputChange('sidestrip', {
                                      ...formData.sidestrip,
                                      showThumbnails: e.target.checked
                                    })}
                                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">Show Thumbnails</div>
                                    <div className="text-sm text-gray-500">
                                      Display gallery cover images in the sidestrip
                                    </div>
                                  </div>
                                </label>

                                <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                  <input
                                    type="checkbox"
                                    checked={formData.sidestrip?.showLabels !== false}
                                    onChange={(e) => handleInputChange('sidestrip', {
                                      ...formData.sidestrip,
                                      showLabels: e.target.checked
                                    })}
                                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">Show Labels</div>
                                    <div className="text-sm text-gray-500">
                                      Display gallery names below thumbnails
                                    </div>
                                  </div>
                                </label>

                                <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                  <input
                                    type="checkbox"
                                    checked={formData.sidestrip?.showCounts || false}
                                    onChange={(e) => handleInputChange('sidestrip', {
                                      ...formData.sidestrip,
                                      showCounts: e.target.checked
                                    })}
                                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">Show Item Counts</div>
                                    <div className="text-sm text-gray-500">
                                      Display number of items in each gallery
                                    </div>
                                  </div>
                                </label>

                                <label className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                                  <input
                                    type="checkbox"
                                    checked={formData.sidestrip?.collapsible || false}
                                    onChange={(e) => handleInputChange('sidestrip', {
                                      ...formData.sidestrip,
                                      collapsible: e.target.checked
                                    })}
                                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <div className="flex-1">
                                    <div className="font-medium text-gray-900">Collapsible</div>
                                    <div className="text-sm text-gray-500">
                                      Allow users to hide/show the sidestrip
                                    </div>
                                  </div>
                                </label>
                              </div>

                              {/* Width Configuration */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                  Sidestrip Width
                                </label>
                                <select
                                  value={formData.sidestrip?.width || 'medium'}
                                  onChange={(e) => handleInputChange('sidestrip', {
                                    ...formData.sidestrip,
                                    width: e.target.value
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="narrow">Narrow (64px)</option>  {/* Updated from 80px */}
                                  <option value="medium">Medium (80px)</option>  {/* Updated from 120px */}
                                  <option value="wide">Wide (96px)</option>      {/* Updated from 160px */}
                                </select>
                              </div>

                              {/* Thumbnail Size */}
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                  Thumbnail Size
                                </label>
                                <select
                                  value={formData.sidestrip?.thumbnailSize || 'medium'}
                                  onChange={(e) => handleInputChange('sidestrip', {
                                    ...formData.sidestrip,
                                    thumbnailSize: e.target.value
                                  })}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                >
                                  <option value="small">Small (40px)</option>   {/* Updated from 60px */}
                                  <option value="medium">Medium (48px)</option> {/* Updated from 80px */}
                                  <option value="large">Large (56px)</option>   {/* Updated from 100px */}
                                </select>
                              </div>
                            </>
                          )}
                        </div>
                      </div>

                    <div className="bg-white p-6 rounded-lg border border-gray-200">
                      <div className="flex items-center gap-2 mb-4">
                        <RotateCcw className="h-5 w-5 text-blue-600" />
                        <h4 className="text-lg font-semibold text-gray-900">Flip Card Settings</h4>
                      </div>
                      
                      <div className="space-y-4">
                        {/* Enable Flip Cards Toggle */}
                        <label className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <span className="font-medium text-gray-900">Enable Flip Cards</span>
                            <p className="text-sm text-gray-600">Allow media cards to flip and show additional content on the back</p>
                          </div>
                          <div className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.enableFlipCards || false}
                              onChange={(e) => handleInputChange('enableFlipCards', e.target.checked)}
                              className="sr-only"
                            />
                            <div className={`w-11 h-6 rounded-full transition-colors ${
                              formData.enableFlipCards ? 'bg-blue-600' : 'bg-gray-200'
                            }`}>
                              <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
                                formData.enableFlipCards ? 'translate-x-5' : 'translate-x-0'
                              }`} />
                            </div>
                          </div>
                        </label>

                        {/* Flip Card Options - Only show when enabled */}
                        {formData.enableFlipCards && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
                            {/* Flip Direction */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Flip Direction
                              </label>
                              <div className="grid grid-cols-2 gap-3">
                                {[
                                  { value: 'horizontal', label: 'Horizontal', icon: '↔️' },
                                  { value: 'vertical', label: 'Vertical', icon: '↕️' }
                                ].map(direction => (
                                  <div
                                    key={direction.value}
                                    onClick={() => handleInputChange('flipDirection', direction.value)}
                                    className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center ${
                                      formData.flipDirection === direction.value
                                        ? 'border-blue-500 bg-blue-100'
                                        : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                  >
                                    <div className="text-lg mb-1">{direction.icon}</div>
                                    <div className="text-sm font-medium">{direction.label}</div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Flip Duration */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Animation Speed
                              </label>
                              <select
                                value={formData.flipDuration || '0.6s'}
                                onChange={(e) => handleInputChange('flipDuration', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="0.3s">Fast (0.3s)</option>
                                <option value="0.6s">Normal (0.6s)</option>
                                <option value="1s">Slow (1s)</option>
                              </select>
                            </div>

                            {/* Auto-flip on hover (optional) */}
                            <label className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                              <div>
                                <span className="font-medium text-gray-900 text-sm">Auto-flip on Hover</span>
                                <p className="text-xs text-gray-600">Automatically flip cards when users hover over them</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={formData.autoFlipOnHover || false}
                                onChange={(e) => handleInputChange('autoFlipOnHover', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                            </label>

                            {/* Show flip indicator */}
                            <label className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                              <div>
                                <span className="font-medium text-gray-900 text-sm">Show Flip Indicator</span>
                                <p className="text-xs text-gray-600">Display a visual hint that cards can be flipped</p>
                              </div>
                              <input
                                type="checkbox"
                                checked={formData.showFlipIndicator !== false} // Default to true
                                onChange={(e) => handleInputChange('showFlipIndicator', e.target.checked)}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'interactions' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">User Interactions</h3>
                  
                  {/* Info about interaction limits */}
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="h-5 w-5 text-blue-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-900">Interaction Display Priority</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          Only the top 4 interactions will be visible by default. Users can click "More" to see additional options.
                          Interactions are prioritized in the order shown below.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    {[
                      { 
                        key: 'allowLikes', 
                        label: 'Allow Likes', 
                        desc: 'Users can like media items',
                        icon: Heart,
                        priority: 1
                      },
                      { 
                        key: 'allowComments', 
                        label: 'Allow Comments', 
                        desc: 'Users can comment on media items',
                        icon: MessageCircle,
                        priority: 2
                      },
                      { 
                        key: 'allowSharing', 
                        label: 'Allow Sharing', 
                        desc: 'Users can share media items and the album',
                        icon: Share2,
                        priority: 5
                      },
                      { 
                        key: 'allowDownloads', 
                        label: 'Allow Downloads', 
                        desc: 'Users can download media files',
                        icon: Upload,
                        priority: 6
                      },
                      { 
                        key: 'showViewCounts', 
                        label: 'Show View Counts', 
                        desc: 'Display how many times media has been viewed',
                        icon: Eye,
                        priority: 7
                      },
                      { 
                        key: 'showInteractionStats', 
                        label: 'Show Interaction Stats', 
                        desc: 'Display likes, comments and other interaction counts',
                        icon: BarChart3,
                        priority: 8
                      }
                    ].map(({ key, label, desc, icon: Icon, priority }) => (
                      <label key={key} className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData[key]}
                          onChange={(e) => handleInputChange(key, e.target.checked)}
                          className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div className="flex items-start gap-3 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-1 rounded">
                              #{priority}
                            </span>
                            <Icon className="h-5 w-5 text-gray-600" />
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{desc}</div>
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                  
                  {/* Custom Priority Settings */}
                  <div className="mt-6 p-4 border border-gray-200 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Interaction Priority Override</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Customize which interactions appear first when more than 4 are enabled.
                    </p>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="customPriority"
                        checked={formData.useCustomPriority || false}
                        onChange={(e) => handleInputChange('useCustomPriority', e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor="customPriority" className="text-sm font-medium text-gray-700">
                        Enable custom interaction priority order
                      </label>
                    </div>
                    
                    {formData.useCustomPriority && (
                      <div className="mt-3 text-sm text-blue-600">
                        <Info className="h-4 w-4 inline mr-1" />
                        Custom priority ordering feature coming soon
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'transactions' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Transaction Settings</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Configure booking and shopping options for your album. These will appear as action buttons at the bottom of your album.
                  </p>
                </div>

                {/* Booking Configuration */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <h4 className="text-lg font-medium text-gray-900">Booking System</h4>
                  </div>
                  
                  <label className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      checked={formData.transactions.booking.enabled}
                      onChange={(e) => handleNestedInputChange('transactions', 'booking', {
                        ...formData.transactions.booking,
                        enabled: e.target.checked
                      })}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="font-medium text-gray-700">Enable Booking</span>
                  </label>

                  {formData.transactions.booking.enabled && (
                    <div className="space-y-4 ml-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Button Title
                        </label>
                        <input
                          type="text"
                          value={formData.transactions.booking.title}
                          onChange={(e) => handleNestedInputChange('transactions', 'booking', {
                            ...formData.transactions.booking,
                            title: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Book Now"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={formData.transactions.booking.description}
                          onChange={(e) => handleNestedInputChange('transactions', 'booking', {
                            ...formData.transactions.booking,
                            description: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="Schedule an appointment"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Booking URL
                        </label>
                        <input
                          type="url"
                          value={formData.transactions.booking.url}
                          onChange={(e) => handleNestedInputChange('transactions', 'booking', {
                            ...formData.transactions.booking,
                            url: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          placeholder="https://calendly.com/your-calendar"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Supported: Calendly, Acuity, Square Appointments, or any booking platform
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Button Color
                        </label>
                        <input
                          type="color"
                          value={formData.transactions.booking.color}
                          onChange={(e) => handleNestedInputChange('transactions', 'booking', {
                            ...formData.transactions.booking,
                            color: e.target.value
                          })}
                          className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Shopping Configuration */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <ShoppingCart className="h-5 w-5 text-green-600" />
                    <h4 className="text-lg font-medium text-gray-900">Shopping System</h4>
                  </div>
                  
                  <label className="flex items-center gap-3 mb-4">
                    <input
                      type="checkbox"
                      checked={formData.transactions.shopping.enabled}
                      onChange={(e) => handleNestedInputChange('transactions', 'shopping', {
                        ...formData.transactions.shopping,
                        enabled: e.target.checked
                      })}
                      className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                    />
                    <span className="font-medium text-gray-700">Enable Shopping</span>
                  </label>

                  {formData.transactions.shopping.enabled && (
                    <div className="space-y-4 ml-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Button Title
                        </label>
                        <input
                          type="text"
                          value={formData.transactions.shopping.title}
                          onChange={(e) => handleNestedInputChange('transactions', 'shopping', {
                            ...formData.transactions.shopping,
                            title: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          placeholder="Shop Now"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <input
                          type="text"
                          value={formData.transactions.shopping.description}
                          onChange={(e) => handleNestedInputChange('transactions', 'shopping', {
                            ...formData.transactions.shopping,
                            description: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          placeholder="Browse our products"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Shopping URL
                        </label>
                        <input
                          type="url"
                          value={formData.transactions.shopping.url}
                          onChange={(e) => handleNestedInputChange('transactions', 'shopping', {
                            ...formData.transactions.shopping,
                            url: e.target.value
                          })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          placeholder="https://your-store.com"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Supported: Shopify, WooCommerce, Square Online, or any e-commerce platform
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Button Color
                        </label>
                        <input
                          type="color"
                          value={formData.transactions.shopping.color}
                          onChange={(e) => handleNestedInputChange('transactions', 'shopping', {
                            ...formData.transactions.shopping,
                            color: e.target.value
                          })}
                          className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'galleries' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Gallery Settings</h3>
                  
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Default Gallery Name
                      </label>
                      <input
                        type="text"
                        value={formData.defaultGalleryName}
                        onChange={(e) => handleInputChange('defaultGalleryName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Main Gallery"
                      />
                    </div>

                    <div className="space-y-4">
                      {[
                        { 
                          key: 'autoCreateFirstGallery', 
                          label: 'Auto-create First Gallery', 
                          desc: 'Automatically create a default gallery when the album is created' 
                        },
                        { 
                          key: 'showGalleryTabs', 
                          label: 'Show Gallery Navigation', 
                          desc: 'Display tabs for switching between galleries' 
                        },
                        { 
                          key: 'allowMultipleGalleries', 
                          label: 'Allow Multiple Galleries', 
                          desc: 'Users can create additional galleries within the album' 
                        }
                      ].map(({ key, label, desc }) => (
                        <label key={key} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => handleInputChange(key, e.target.checked)}
                            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'access' && (
              <PrivacySection
                bookId={bookId}
                formData={formData}
                onInputChange={handleInputChange}
                templateType="organisation"
              />
            )}

            {activeTab === 'seo' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    SEO & Custom Domain
                  </h3>
                  
                  <div className="space-y-6">
                    {/* Custom Domain Section - ENHANCED */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="text-md font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <Link className="h-4 w-4" />
                        Custom Domain & Path Configuration
                      </h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Custom Domain
                          </label>
                          <input
                            type="text"
                            value={formData.customDomain}
                            onChange={(e) => handleInputChange('customDomain', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="photos.yourdomain.com"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Your custom domain (without https://)
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Custom Path <span className="text-gray-400">(Optional)</span>
                          </label>
                          <input
                            type="text"
                            value={formData.customPath}
                            onChange={(e) => handleInputChange('customPath', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="gallery or wedding2024"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Path after domain (letters, numbers, hyphens only)
                          </p>
                        </div>
                      </div>

                      {/* URL Preview */}
                      {previewUrl && (
                        <div className="bg-white border rounded-lg p-3 mb-4">
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-gray-500">Your album will be available at:</span>
                            <a
                              href={previewUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                            >
                              {previewUrl}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Domain Configuration Instructions */}
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <h5 className="text-sm font-medium text-yellow-800 mb-2">Domain Configuration Guide:</h5>
                        <ul className="text-xs text-yellow-700 space-y-1">
                          <li>• <strong>Base Domain:</strong> Use just "photos.yourdomain.com" for the entire domain</li>
                          <li>• <strong>Path-based:</strong> Use "yourdomain.com" + path "gallery" for yourdomain.com/gallery</li>
                          <li>• <strong>DNS:</strong> Point your domain/subdomain to our servers in your DNS settings</li>
                          <li>• <strong>SSL:</strong> HTTPS certificates are automatically managed</li>
                        </ul>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Title
                      </label>
                      <input
                        type="text"
                        value={formData.seoTitle}
                        onChange={(e) => handleInputChange('seoTitle', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Custom title for search engines"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Description
                      </label>
                      <textarea
                        value={formData.seoDescription}
                        onChange={(e) => handleInputChange('seoDescription', e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Description for search engines and social media"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        SEO Keywords
                      </label>
                      <input
                        type="text"
                        value={formData.seoKeywords}
                        onChange={(e) => handleInputChange('seoKeywords', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="photo, gallery, portfolio, wedding"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Separate keywords with commas
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-900">SEO Images</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Favicon Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Favicon
                  </label>
                  <div className="space-y-3">
                    {faviconPreview && (
                      <div className="relative">
                        <img
                          src={faviconPreview}
                          alt="Favicon preview"
                          className="w-8 h-8 object-cover border rounded"
                        />
                        <button
                          onClick={() => {
                            setFaviconFile(null);
                            setFaviconPreview('');
                            handleInputChange('favicon', '');
                          }}
                          className="absolute -top-2 -right-2 p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    
                    <div>
                            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-2">
                              <label htmlFor="favicon-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                <Upload className="h-4 w-4 inline mr-2" />
                                Upload Favicon
                              </label>
                              <input
                                id="favicon-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleFaviconSelect}
                                className="hidden"
                              />
                            </div>
                          </div>
                    <p className="text-xs text-gray-500">
                      ICO, PNG, or SVG format. Recommended: 32x32px
                    </p>
                  </div>
                </div>

                {/* OG Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Open Graph Image
                  </label>
                  <div className="space-y-3">
                    {ogImagePreview && (
                      <div className="relative">
                        <img
                          src={ogImagePreview}
                          alt="OG image preview"
                          className="w-32 h-16 object-cover border rounded"
                        />
                        <button
                          onClick={() => {
                            setOgImageFile(null);
                            setOgImagePreview('');
                            handleInputChange('ogImage', '');
                          }}
                          className="absolute -top-2 -right-2 p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                    
                    <div>
                            <ImageIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="mt-2">
                              <label htmlFor="ogimage-upload" className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                <Upload className="h-4 w-4 inline mr-2" />
                                Upload Cover
                              </label>
                              <input
                                id="ogimage-upload"
                                type="file"
                                accept="image/*"
                                onChange={handleOgImageSelect}
                                className="hidden"
                              />
                            </div>
                          </div>
                    <p className="text-xs text-gray-500">
                      Used when sharing on social media. Recommended: 1200x630px
                    </p>
                  </div>
                </div>
              </div>
            </div>
              </div>
            )}

            {/* Voice & Audio Settings Tab */}
            {activeTab === 'voice' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Voice & Audio Features</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Add spoken context to your album, galleries, and media items for immersive storytelling
                  </p>
                </div>

                {/* Master Voice Toggle */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center">
                        <Volume2 className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Enable Voice Features</h4>
                        <p className="text-sm text-gray-600">
                          Turn on voice narration and audio context throughout your album
                        </p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.voiceSettings?.enabled || false}
                        onChange={(e) => handleInputChange('voiceSettings.enabled', e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-14 h-7 bg-gray-200 peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
                    </label>
                  </div>
                </div>

                {/* Conditional Voice Settings Content */}
                {formData.voiceSettings?.enabled && (
                  <div className="space-y-6 animate-in fade-in-50 duration-300">
                    
                    {/* Album-Level Voice Narration */}
                    <div className="border border-gray-200 rounded-lg p-6 space-y-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <Mic className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Album Narration</h4>
                          <p className="text-sm text-gray-500">Add a voice introduction to your entire album</p>
                        </div>
                      </div>

                      <div className="space-y-3 pl-13">
                        <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">Enable Album Voice</div>
                            <div className="text-xs text-gray-500">Allow recording of album-level narration</div>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.voiceSettings?.albumNarration?.enabled || false}
                            onChange={(e) => handleInputChange('voiceSettings.albumNarration.enabled', e.target.checked)}
                            className="rounded text-purple-600 focus:ring-purple-500"
                          />
                        </label>

                        {formData.voiceSettings?.albumNarration?.enabled && (
                          <div className="space-y-3 pl-4 border-l-2 border-purple-200">
                            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                              <div className="flex-1">
                                <div className="text-sm text-gray-900">Auto-play on page load</div>
                                <div className="text-xs text-gray-500">Start playing automatically when visitors arrive</div>
                              </div>
                              <input
                                type="checkbox"
                                checked={formData.voiceSettings?.albumNarration?.autoPlay || false}
                                onChange={(e) => handleInputChange('voiceSettings.albumNarration.autoPlay', e.target.checked)}
                                className="rounded text-purple-600 focus:ring-purple-500"
                              />
                            </label>

                            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                              <div className="flex-1">
                                <div className="text-sm text-gray-900">Show Transcript</div>
                                <div className="text-xs text-gray-500">Display text version of the narration</div>
                              </div>
                              <input
                                type="checkbox"
                                checked={formData.voiceSettings?.albumNarration?.showTranscript !== false}
                                onChange={(e) => handleInputChange('voiceSettings.albumNarration.showTranscript', e.target.checked)}
                                className="rounded text-purple-600 focus:ring-purple-500"
                              />
                            </label>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Player Position
                              </label>
                              <select
                                value={formData.voiceSettings?.albumNarration?.playerPosition || 'top-right'}
                                onChange={(e) => handleInputChange('voiceSettings.albumNarration.playerPosition', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                              >
                                <option value="top-right">Top Right</option>
                                <option value="top-left">Top Left</option>
                                <option value="bottom-right">Bottom Right</option>
                                <option value="bottom-left">Bottom Left</option>
                                <option value="floating">Floating</option>
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Gallery-Level Voice Narration */}
                    <div className="border border-gray-200 rounded-lg p-6 space-y-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <BookOpen className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Gallery Narration</h4>
                          <p className="text-sm text-gray-500">Add voice stories to individual galleries</p>
                        </div>
                      </div>

                      <div className="space-y-3 pl-13">
                        <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">Enable Gallery Voice</div>
                            <div className="text-xs text-gray-500">Allow narration for each gallery</div>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.voiceSettings?.galleryNarration?.enabled || false}
                            onChange={(e) => handleInputChange('voiceSettings.galleryNarration.enabled', e.target.checked)}
                            className="rounded text-purple-600 focus:ring-purple-500"
                          />
                        </label>

                        {formData.voiceSettings?.galleryNarration?.enabled && (
                          <div className="space-y-3 pl-4 border-l-2 border-blue-200">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Display Mode
                              </label>
                              <select
                                value={formData.voiceSettings?.galleryNarration?.displayMode || 'banner'}
                                onChange={(e) => handleInputChange('voiceSettings.galleryNarration.displayMode', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                              >
                                <option value="banner">Banner (Top of Gallery)</option>
                                <option value="overlay">Overlay (Above Media)</option>
                                <option value="sidebar">Sidebar</option>
                                <option value="floating">Floating Player</option>
                              </select>
                              <p className="text-xs text-gray-500 mt-1">
                                How gallery voice player appears to viewers
                              </p>
                            </div>

                            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                              <div className="flex-1">
                                <div className="text-sm text-gray-900">Enable Voice Segments</div>
                                <div className="text-xs text-gray-500">Break gallery story into chapters</div>
                              </div>
                              <input
                                type="checkbox"
                                checked={formData.voiceSettings?.galleryNarration?.enableSegments || false}
                                onChange={(e) => handleInputChange('voiceSettings.galleryNarration.enableSegments', e.target.checked)}
                                className="rounded text-purple-600 focus:ring-purple-500"
                              />
                            </label>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Media-Level Voice Notes */}
                    <div className="border border-gray-200 rounded-lg p-6 space-y-4">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Media Voice Notes</h4>
                          <p className="text-sm text-gray-500">Let viewers add voice context to individual photos</p>
                        </div>
                      </div>

                      <div className="space-y-3 pl-13">
                        <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                          <div className="flex-1">
                            <div className="font-medium text-sm text-gray-900">Enable Voice Notes</div>
                            <div className="text-xs text-gray-500">Allow voice recordings on media items</div>
                          </div>
                          <input
                            type="checkbox"
                            checked={formData.voiceSettings?.mediaVoiceNotes?.enabled || false}
                            onChange={(e) => handleInputChange('voiceSettings.mediaVoiceNotes.enabled', e.target.checked)}
                            className="rounded text-purple-600 focus:ring-purple-500"
                          />
                        </label>

                        {formData.voiceSettings?.mediaVoiceNotes?.enabled && (
                          <div className="space-y-3 pl-4 border-l-2 border-green-200">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Maximum Duration (seconds)
                              </label>
                              <input
                                type="number"
                                value={formData.voiceSettings?.mediaVoiceNotes?.maxDuration || 60}
                                onChange={(e) => handleInputChange('voiceSettings.mediaVoiceNotes.maxDuration', Number(e.target.value))}
                                min="15"
                                max="180"
                                step="15"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                Limit recording length (15-180 seconds)
                              </p>
                            </div>

                            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                              <div className="flex-1">
                                <div className="text-sm text-gray-900">Allow Multiple Speakers</div>
                                <div className="text-xs text-gray-500">Multiple people can add voice notes to same photo</div>
                              </div>
                              <input
                                type="checkbox"
                                checked={formData.voiceSettings?.mediaVoiceNotes?.allowMultipleSpeakers !== false}
                                onChange={(e) => handleInputChange('voiceSettings.mediaVoiceNotes.allowMultipleSpeakers', e.target.checked)}
                                className="rounded text-purple-600 focus:ring-purple-500"
                              />
                            </label>

                            <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                              <div className="flex-1">
                                <div className="text-sm text-gray-900">Auto-transcribe</div>
                                <div className="text-xs text-gray-500">Generate text from voice notes (requires API)</div>
                              </div>
                              <input
                                type="checkbox"
                                checked={formData.voiceSettings?.mediaVoiceNotes?.autoTranscribe || false}
                                onChange={(e) => handleInputChange('voiceSettings.mediaVoiceNotes.autoTranscribe', e.target.checked)}
                                className="rounded text-purple-600 focus:ring-purple-500"
                              />
                            </label>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Voice Note Indicator Style
                              </label>
                              <select
                                value={formData.voiceSettings?.mediaVoiceNotes?.indicatorStyle || 'waveform'}
                                onChange={(e) => handleInputChange('voiceSettings.mediaVoiceNotes.indicatorStyle', e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                              >
                                <option value="waveform">Animated Waveform</option>
                                <option value="badge">Mic Badge</option>
                                <option value="corner-flag">Corner Flag</option>
                                <option value="overlay-hint">Hover Overlay</option>
                                <option value="minimal">Minimal Icon</option>
                              </select>
                              <p className="text-xs text-gray-500 mt-1">
                                How voice notes are indicated on media
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Voice Permissions - UPDATED FOR DISTRIBUTED PERMISSIONS */}
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Voice Permissions</h3>
                        <p className="text-sm text-gray-600 mb-6">
                          Control who can record voice content at different levels
                        </p>
                      </div>

                      {/* Album-Level Permissions */}
                      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-purple-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">Album Narration Permissions</h4>
                            <p className="text-sm text-gray-500">Who can record album-level voice introductions</p>
                          </div>
                        </div>

                        <div className="space-y-3 pl-13">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Who Can Record Album Voice
                            </label>
                            <select
                              value={formData.voiceSettings?.albumNarration?.permissions?.canRecord || 'owner-only'}
                              onChange={(e) => handleInputChange('voiceSettings.albumNarration.permissions.canRecord', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="owner-only">Owner Only</option>
                              <option value="collaborators">Owner & Collaborators</option>
                              <option value="authenticated">Any Signed-in User</option>
                              <option value="anyone">Anyone (Including Guests)</option>
                            </select>
                          </div>

                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                            <div className="flex-1">
                              <div className="text-sm text-gray-900">Require Moderation</div>
                              <div className="text-xs text-gray-500">Review album voice before publishing</div>
                            </div>
                            <input
                              type="checkbox"
                              checked={formData.voiceSettings?.albumNarration?.permissions?.requireModeration || false}
                              onChange={(e) => handleInputChange('voiceSettings.albumNarration.permissions.requireModeration', e.target.checked)}
                              className="rounded text-purple-600 focus:ring-purple-500"
                            />
                          </label>
                        </div>
                      </div>

                      {/* Gallery-Level Permissions */}
                      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">Gallery Narration Permissions</h4>
                            <p className="text-sm text-gray-500">Default permissions for gallery-level voice (can be overridden per gallery)</p>
                          </div>
                        </div>

                        <div className="space-y-3 pl-13">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Default Gallery Voice Permissions
                            </label>
                            <select
                              value={formData.voiceSettings?.galleryNarration?.permissions?.canRecord || 'owner-only'}
                              onChange={(e) => handleInputChange('voiceSettings.galleryNarration.permissions.canRecord', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="owner-only">Owner Only</option>
                              <option value="collaborators">Owner & Collaborators</option>
                              <option value="authenticated">Any Signed-in User</option>
                              <option value="anyone">Anyone (Including Guests)</option>
                            </select>
                            <p className="text-xs text-gray-500 mt-1">
                              Can be customized for individual galleries in Gallery Manager
                            </p>
                          </div>

                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                            <div className="flex-1">
                              <div className="text-sm text-gray-900">Require Moderation</div>
                              <div className="text-xs text-gray-500">Review gallery voice before publishing</div>
                            </div>
                            <input
                              type="checkbox"
                              checked={formData.voiceSettings?.galleryNarration?.permissions?.requireModeration || false}
                              onChange={(e) => handleInputChange('voiceSettings.galleryNarration.permissions.requireModeration', e.target.checked)}
                              className="rounded text-purple-600 focus:ring-purple-500"
                            />
                          </label>
                        </div>
                      </div>

                      {/* Media-Level Permissions */}
                      <div className="border border-gray-200 rounded-lg p-6 space-y-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">Media Voice Note Permissions</h4>
                            <p className="text-sm text-gray-500">Who can add voice notes to individual media items</p>
                          </div>
                        </div>

                        <div className="space-y-3 pl-13">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Who Can Add Voice Notes
                            </label>
                            <select
                              value={formData.voiceSettings?.mediaVoiceNotes?.permissions?.canRecord || 'authenticated'}
                              onChange={(e) => handleInputChange('voiceSettings.mediaVoiceNotes.permissions.canRecord', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
                            >
                              <option value="owner-only">Owner Only</option>
                              <option value="collaborators">Owner & Collaborators</option>
                              <option value="authenticated">Any Signed-in User</option>
                              <option value="anyone">Anyone (Including Guests)</option>
                            </select>
                          </div>

                          <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                            <div className="flex-1">
                              <div className="text-sm text-gray-900">Require Moderation</div>
                              <div className="text-xs text-gray-500">Review media voice notes before publishing</div>
                            </div>
                            <input
                              type="checkbox"
                              checked={formData.voiceSettings?.mediaVoiceNotes?.permissions?.requireModeration || false}
                              onChange={(e) => handleInputChange('voiceSettings.mediaVoiceNotes.permissions.requireModeration', e.target.checked)}
                              className="rounded text-purple-600 focus:ring-purple-500"
                            />
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Voice Analytics */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                      <div className="flex items-center gap-3 mb-3">
                        <BarChart3 className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold text-gray-900">Voice Analytics</h4>
                      </div>
                      <div className="space-y-2 text-sm text-gray-700">
                        <div className="flex justify-between">
                          <span>Total Voice Notes:</span>
                          <span className="font-semibold">{formData.stats?.totalVoiceNotes || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Playbacks:</span>
                          <span className="font-semibold">{formData.stats?.voicePlaybacks || 0}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Avg. Listen Rate:</span>
                          <span className="font-semibold">
                            {formData.stats?.avgListenRate ? `${formData.stats.avgListenRate}%` : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                )}

                {/* Info Banner */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3">
                  <Info className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-purple-900">
                    <p className="font-medium mb-1">💡 Voice Features Benefits</p>
                    <ul className="list-disc list-inside space-y-1 text-purple-800">
                      <li>Create emotional connections through spoken stories</li>
                      <li>Make albums accessible in multiple languages</li>
                      <li>Capture family history and heritage authentically</li>
                      <li>Increase engagement with personalized narratives</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'discovery' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Discovery & Marketplace</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Enable Pinterest-style discovery features for your showcase album
                  </p>
                </div>

                {/* Enable Discovery */}
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-white rounded-lg shadow-sm">
                      <Compass className="h-6 w-6 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">Discovery Mode</h4>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.discoveryEnabled || false}
                            onChange={(e) => handleInputChange('discoveryEnabled', e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                        </label>
                      </div>
                      <p className="text-sm text-gray-600">
                        Make your showcase discoverable on the platform marketplace. Users can browse, search, and explore your content.
                      </p>
                    </div>
                  </div>
                </div>

                {formData.discoveryEnabled && (
                  <div className="space-y-6">
                    {/* Showcase Subcategory */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Showcase Type
                      </label>
                      <select
                        value={formData.showcaseSubcategory || ''}
                        onChange={(e) => handleInputChange('showcaseSubcategory', e.target.value)}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="">Select showcase type...</option>
                        <option value="fashion">Fashion</option>
                        <option value="beauty">Beauty</option>
                        <option value="products">Products</option>
                        <option value="projects">Projects</option>
                        <option value="photography">Photography</option>
                        <option value="art">Art & Design</option>
                      </select>
                    </div>

                    {/* Discovery Tags */}
                    <TagInput
                      label="Discovery Tags"
                      tags={formData.discoveryTags || []}
                      onChange={(tags) => handleInputChange('discoveryTags', tags)}
                      maxTags={10}
                      placeholder="Add tags like 'minimalist', 'vintage', 'modern'..."
                      showCount={true}
                    />

                    {/* Featured Items Limit */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Featured Items Limit
                        <span className="text-gray-500 ml-2">(Shown first in discovery)</span>
                      </label>
                      <input
                        type="number"
                        min="3"
                        max="12"
                        value={formData.featuredItemsLimit || 6}
                        onChange={(e) => handleInputChange('featuredItemsLimit', parseInt(e.target.value))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    {/* Discovery Filters */}
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Enable Filters</h4>
                      <div className="grid grid-cols-2 gap-4">
                        {[
                          { key: 'category', label: 'Category' },
                          { key: 'tags', label: 'Tags' },
                          { key: 'dateRange', label: 'Date Range' },
                          { key: 'featured', label: 'Featured' }
                        ].map(filter => (
                          <label key={filter.key} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.discoveryFilters?.[filter.key] || false}
                              onChange={(e) => {
                                const newFilters = { ...(formData.discoveryFilters || {}) };
                                newFilters[filter.key] = e.target.checked;
                                handleInputChange('discoveryFilters', newFilters);
                              }}
                              className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />
                            <span className="text-sm text-gray-700">{filter.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Grid Layout Options */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Discovery Layout
                      </label>
                      <div className="grid grid-cols-3 gap-3">
                        {[
                          { value: 'masonry', label: 'Masonry', icon: Grid, desc: 'Pinterest-style' },
                          { value: 'uniform', label: 'Uniform Grid', icon: LayoutGrid, desc: 'Equal sizes' },
                          { value: 'magazine', label: 'Magazine', icon: Layout, desc: 'Mixed sizes' }
                        ].map(layout => {
                          const LayoutIcon = layout.icon;
                          return (
                            <button
                              key={layout.value}
                              type="button"
                              onClick={() => handleInputChange('discoveryLayout', layout.value)}
                              className={`p-4 rounded-lg border-2 text-center transition-all ${
                                formData.discoveryLayout === layout.value
                                  ? 'border-purple-500 bg-purple-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <LayoutIcon className="h-6 w-6 mx-auto mb-2 text-gray-600" />
                              <div className="font-medium text-sm text-gray-900">{layout.label}</div>
                              <div className="text-xs text-gray-500">{layout.desc}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Preview Settings */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <Sparkles className="h-5 w-5 text-blue-600 mt-0.5" />
                        <div>
                          <h4 className="font-medium text-blue-900 mb-1">Discovery Preview</h4>
                          <p className="text-sm text-blue-700">
                            Your showcase will appear in discovery feeds with your cover image, title, and first {formData.featuredItemsLimit || 6} items as featured content.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* SEO Images Section */}

            {activeTab === 'advanced' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Advanced Settings</h3>
                  
                  <div className="space-y-6">
                    <div className="space-y-4">
                      {[
                        { 
                          key: 'enableAnalytics', 
                          label: 'Enable Analytics', 
                          desc: 'Track views, interactions, and user behavior' 
                        },
                        { 
                          key: 'enableRightClickProtection', 
                          label: 'Right-click Protection', 
                          desc: 'Disable right-click context menu to discourage image saving' 
                        }
                      ].map(({ key, label, desc }) => (
                        <label key={key} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData[key]}
                            onChange={(e) => handleInputChange(key, e.target.checked)}
                            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{label}</div>
                            <div className="text-sm text-gray-500">{desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>

                    {/* Custom CSS */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom CSS
                      </label>
                      <textarea
                        value={formData.customCSS}
                        onChange={(e) => handleInputChange('customCSS', e.target.value)}
                        rows={8}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                        placeholder={`/* Add your custom CSS here */
.album-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.media-card {
  transition: transform 0.3s ease;
}

.media-card:hover {
  transform: translateY(-4px);
}`}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Add custom CSS to further customize the album appearance
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'preview' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Preview</h3>
                  
                  {/* Preview Mode Selector */}
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm text-gray-600">Preview as:</span>
                    {[
                      { value: 'desktop', label: 'Desktop', icon: Monitor },
                      { value: 'tablet', label: 'Tablet', icon: Tablet },
                      { value: 'mobile', label: 'Mobile', icon: Smartphone }
                    ].map(({ value, label, icon: Icon }) => (
                      <button
                        key={value}
                        onClick={() => setPreviewMode(value)}
                        className={`flex items-center gap-1 px-3 py-1 rounded text-sm transition-colors ${
                          previewMode === value 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* Preview Container */}
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div 
                      className={`transition-all duration-300 mx-auto bg-white ${
                        previewMode === 'mobile' 
                          ? 'max-w-sm' 
                          : previewMode === 'tablet' 
                          ? 'max-w-2xl' 
                          : 'max-w-full'
                      }`}
                      style={previewStyles}
                    >
                      {/* Preview Header */}
                      <div 
                        className={`relative flex items-center justify-between p-4 ${getShadowClass()}`}
                        style={{ 
                          backgroundColor: formData.headerColor,
                          color: formData.headerTextColor,
                          borderRadius: `${formData.headerRadius} ${formData.headerRadius} 0 0`,
                          backgroundImage: headerBackgroundPreview ? `url(${headerBackgroundPreview})` : 'none',
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        {headerBackgroundPreview && (
                          <div className="absolute inset-0 bg-black bg-opacity-30 rounded-t-lg" />
                        )}
                        
                        <div className="relative z-10 flex items-center gap-3">
                          {logoPreview && (
                            <img src={logoPreview} alt="Logo" className="h-8 w-auto" />
                          )}
                          <h1 className="text-xl font-bold" style={{ color: formData.headerTextColor }}>
                            {formData.title || 'Album Title'}
                          </h1>
                        </div>
                        
                        <div className="relative z-10 flex items-center gap-2">
                          {formData.allowSharing && <Share2 className="h-5 w-5 opacity-80" />}
                          {formData.showViewCounts && <Eye className="h-5 w-5 opacity-80" />}
                        </div>
                      </div>

                      {/* Preview Content */}
                      <div className="p-4" style={{ backgroundColor: formData.backgroundColor }}>
                        {formData.description && (
                          <p className="text-gray-600 mb-4">{formData.description}</p>
                        )}
                        
                        {/* Sample Media Cards */}
                        <div className={`grid gap-4 ${
                          previewMode === 'mobile' 
                            ? `grid-cols-${formData.columnsBreakpoints.mobile}` 
                            : previewMode === 'tablet' 
                            ? `grid-cols-${formData.columnsBreakpoints.tablet}` 
                            : `grid-cols-${formData.columnsBreakpoints.desktop}`
                        }`}>
                          {[1, 2, 3, 4].slice(0, previewMode === 'mobile' ? formData.columnsBreakpoints.mobile * 2 : 4).map(i => (
                            <div 
                              key={i}
                              className={`bg-gray-100 rounded-lg overflow-hidden ${getShadowClass()}`}
                              style={{ borderRadius: formData.cardRadius }}
                            >
                              <div className="aspect-square bg-gradient-to-br from-blue-200 to-purple-200"></div>
                              {(formData.showMediaTitles || formData.showMediaDescriptions || formData.showInteractionStats) && (
                                <div className="p-3">
                                  {formData.showMediaTitles && (
                                    <h3 className="font-medium text-gray-900">Sample Image {i}</h3>
                                  )}
                                  {formData.showMediaDescriptions && (
                                    <p className="text-sm text-gray-600 mt-1">Sample description text</p>
                                  )}
                                  {formData.showInteractionStats && (
                                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                      {formData.allowLikes && (
                                        <span className="flex items-center gap-1">
                                          <Heart className="h-4 w-4" />
                                          12
                                        </span>
                                      )}
                                      {formData.showViewCounts && (
                                        <span className="flex items-center gap-1">
                                          <Eye className="h-4 w-4" />
                                          156
                                        </span>
                                      )}
                                      {formData.allowComments && (
                                        <span className="flex items-center gap-1">
                                          <MessageCircle className="h-4 w-4" />
                                          3
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Album Type: <span className="font-medium">{currentCategory?.label || 'Not Selected'}</span>
            {formData.enabledFields && (
              <span className="ml-4">
                Fields: {Object.values(formData.enabledFields).filter(Boolean).length} enabled
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving || uploading}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};